import { ForbiddenError, NotFoundError, ValidationError } from "../errors.js";
import { getSymbolType, nextPositionLetter, PROFILE_DISPLAYS, resolveCameraType, SYMBOL_TYPES, } from "../camera-plan-geo/symbols.js";
import { LINE_DASHES, LINE_WIDTH_MAX, LINE_WIDTH_MIN, TEXT_SIZE_MAX, TEXT_SIZE_MIN, } from "../camera-plan-geo/draw-style.js";
import { MARKER_ICONS } from "../camera-plan-geo/marker-icons.js";
import { clampImageAspect, imageFractionToLngLat, legacyOverlayToItem, } from "../camera-plan-geo/image-frame.js";
import { defineMutatorWithMeta, defineQueryWithMeta, describeDomains, noArgsSchema, } from "./operation-meta.js";
import { boolean, createBuilder, createSchema, defineMutators, defineQueries, enumeration, json, number, relationships, string, table, } from "@rocicorp/zero";
import { validateEquipmentTagParent } from "./equipment-tag-depth.js";
import { assertCanEditProject, canReadProject, isStaff, scopeToReadableProjects, } from "./project-access.js";
import { z } from "zod";
const personTypes = ["contact", "employee", "freelancer"];
const projectEquipmentGroups = table("projectEquipmentGroups")
    .from("project_equipment_groups")
    .columns({
    id: string(),
    projectID: string().from("project_id"),
    name: string(),
    packingState: enumeration().from("packing_state"),
    riggingState: enumeration().from("rigging_state"),
    displayOrder: number().from("display_order").optional(),
    isCollapsed: boolean().from("is_collapsed").optional(),
    createdAt: number().from("created_at").optional(),
    updatedAt: number().from("updated_at").optional(),
})
    .primaryKey("id");
const projectEquipmentAssignments = table("projectEquipmentAssignments")
    .from("project_equipment_assignments")
    .columns({
    id: string(),
    groupID: string().from("group_id"),
    equipmentID: string().from("equipment_id").optional(),
    temporaryEquipmentID: string().from("temporary_equipment_id").optional(),
    amount: number(),
    notes: string().optional(),
    cameraPlanColumnKey: string().from("camera_plan_column_key").optional(),
    createdAt: number().from("created_at").optional(),
    updatedAt: number().from("updated_at").optional(),
})
    .primaryKey("id");
const projectEquipmentInstanceAssignments = table("projectEquipmentInstanceAssignments")
    .from("project_equipment_instance_assignments")
    .columns({
    id: string(),
    assignmentID: string().from("assignment_id"),
    instanceID: string().from("instance_id"),
    createdAt: number().from("created_at").optional(),
})
    .primaryKey("id");
const temporaryEquipment = table("temporaryEquipment")
    .from("temporary_equipment")
    .columns({
    id: string(),
    projectID: string().from("project_id"),
    name: string(),
    countryOfOrigin: string().from("country_of_origin").optional(),
    carnetAtaDescription: string().from("carnet_ata_description").optional(),
    carnetAtaValue: number().from("carnet_ata_value").optional(),
    weight: number().optional(),
    carnetAtaWeightUnit: string().from("carnet_ata_weight_unit").optional(),
    createdAt: number().from("created_at").optional(),
    updatedAt: number().from("updated_at").optional(),
})
    .primaryKey("id");
const equipment = table("equipment")
    .columns({
    id: string(),
    number: string().optional(),
    name: string(),
    manufacturer: string().optional(),
    icon: string().optional(),
    isSerialized: boolean().from("is_serialized"),
    stockQuantity: number().from("stock_quantity").optional(),
    notes: string().optional(),
    parentEquipmentID: string().from("parent_equipment_id").optional(),
    createdAt: number().from("created_at").optional(),
    updatedAt: number().from("updated_at").optional(),
    deletedAt: number().from("deleted_at").optional(),
    version: number().optional(),
    rentmanID: number().from("rentman_id").optional(),
    weight: number().optional(),
    width: number().optional(),
    height: number().optional(),
    length: number().optional(),
    volume: number().optional(),
    power: number().optional(),
    currentDraw: number().from("current_draw").optional(),
    qrCode: string().from("qr_code").optional(),
    externalRemark: string().from("external_remark").optional(),
    locationInWarehouse: string().from("location_in_warehouse").optional(),
    shortName: string().from("short_name").optional(),
    countryOfOrigin: string().from("country_of_origin").optional(),
    customsTariffNumber: string().from("customs_tariff_number").optional(),
    carnetAtaDescription: string().from("carnet_ata_description").optional(),
    carnetAtaValue: number().from("carnet_ata_value").optional(),
    carnetAtaWeightUnit: string().from("carnet_ata_weight_unit").optional(),
})
    .primaryKey("id");
const equipmentInstances = table("equipmentInstances")
    .from("equipment_instances")
    .columns({
    id: string(),
    equipmentID: string().from("equipment_id"),
    identifier: string(),
    notes: string().optional(),
    acquisitionDate: number().from("acquisition_date").optional(),
    vendorID: string().from("vendor_id").optional(),
    status: enumeration(),
    createdAt: number().from("created_at").optional(),
    updatedAt: number().from("updated_at").optional(),
    qrCode: string().from("qr_code").optional(),
    serialNumber: string().from("serial_number").optional(),
})
    .primaryKey("id");
// Prices live in their own staff-only tables: freelancers read whole
// equipment and instance rows through their project's equipment plan, and
// Zero cannot hide single columns. Only staff queries relate these.
const equipmentPrices = table("equipmentPrices")
    .from("equipment_prices")
    .columns({
    equipmentID: string().from("equipment_id"),
    newPrice: number().from("new_price").optional(),
    rentalPrice: number().from("rental_price").optional(),
    createdAt: number().from("created_at").optional(),
    updatedAt: number().from("updated_at").optional(),
})
    .primaryKey("equipmentID");
const equipmentInstancePrices = table("equipmentInstancePrices")
    .from("equipment_instance_prices")
    .columns({
    instanceID: string().from("instance_id"),
    acquisitionPrice: number().from("acquisition_price").optional(),
    createdAt: number().from("created_at").optional(),
    updatedAt: number().from("updated_at").optional(),
})
    .primaryKey("instanceID");
const equipmentTagAssignments = table("equipmentTagAssignments")
    .from("equipment_tag_assignments")
    .columns({
    id: string(),
    equipmentID: string().from("equipment_id"),
    tagID: string().from("tag_id"),
    createdAt: number().from("created_at").optional(),
})
    .primaryKey("id");
const equipmentTags = table("equipmentTags")
    .from("equipment_tags")
    .columns({
    id: string(),
    name: string(),
    parentID: string().from("parent_id").optional(),
    icon: string().optional(),
    rentmanID: number().from("rentman_id").optional(),
    createdAt: number().from("created_at").optional(),
    updatedAt: number().from("updated_at").optional(),
})
    .primaryKey("id");
const equipmentImages = table("equipmentImages")
    .from("equipment_images")
    .columns({
    id: string(),
    equipmentID: string().from("equipment_id"),
    imageURL: string().from("image_url"),
    displayOrder: number().from("display_order").optional(),
    createdAt: number().from("created_at").optional(),
})
    .primaryKey("id");
const equipmentContents = table("equipmentContents")
    .from("equipment_contents")
    .columns({
    id: string(),
    parentEquipmentID: string().from("parent_equipment_id"),
    // Null for free lines, which only have a name
    childEquipmentID: string().from("child_equipment_id").optional(),
    name: string().optional(),
    quantity: number(),
    createdAt: number().from("created_at").optional(),
    updatedAt: number().from("updated_at").optional(),
})
    .primaryKey("id");
const cameraPlans = table("cameraPlans")
    .from("camera_plans")
    .columns({
    id: string(),
    projectID: string().from("project_id"),
    name: string(),
    description: string().optional(),
    eventDate: number().from("event_date").optional(),
    sortOrder: number().from("sort_order").optional(),
    subtitle: string().optional(),
    markSnapDistanceM: number().from("mark_snap_distance_m"),
    markMergeDistanceM: number().from("mark_merge_distance_m"),
    // share_token is deliberately not synced: collaborators read plans
    // through Zero too. Staff get it from /api/camera-plans/:id/share.
    createdAt: number().from("created_at").optional(),
    updatedAt: number().from("updated_at").optional(),
})
    .primaryKey("id");
/**
 * How a plan is shown: a map, an uploaded image or an embedded page. Map
 * and image visualizations have their own items and tracks; cameras are
 * shared by all visualizations of a plan.
 */
const cameraPlanVisualizations = table("cameraPlanVisualizations")
    .from("camera_plan_visualizations")
    .columns({
    id: string(),
    cameraPlanID: string().from("camera_plan_id"),
    name: string(),
    vizType: enumeration().from("viz_type"),
    imageURL: string().from("image_url").optional(),
    imageAspect: number().from("image_aspect").optional(),
    embedURL: string().from("embed_url").optional(),
    viewDefaults: json().from("view_defaults"),
    convertLegacy: boolean().from("convert_legacy"),
    sortOrder: number().from("sort_order"),
    createdAt: number().from("created_at").optional(),
    updatedAt: number().from("updated_at").optional(),
})
    .primaryKey("id");
const cameraPlanTracks = table("cameraPlanTracks")
    .from("camera_plan_tracks")
    .columns({
    id: string(),
    cameraPlanID: string().from("camera_plan_id"),
    visualizationID: string().from("visualization_id"),
    name: string(),
    color: string(),
    sortOrder: number().from("sort_order"),
    geometry: json(),
    lengthM: number().from("length_m"),
    ascentM: number().from("ascent_m").optional(),
    descentM: number().from("descent_m").optional(),
    minEleM: number().from("min_ele_m").optional(),
    maxEleM: number().from("max_ele_m").optional(),
    elevationSource: enumeration()
        .from("elevation_source")
        .optional(),
    profile: json(),
    reversed: boolean(),
    sourceFileURL: string().from("source_file_url").optional(),
    sourceFileName: string().from("source_file_name").optional(),
    createdAt: number().from("created_at").optional(),
    updatedAt: number().from("updated_at").optional(),
})
    .primaryKey("id");
const cameraPlanItems = table("cameraPlanItems")
    .from("camera_plan_items")
    .columns({
    id: string(),
    cameraPlanID: string().from("camera_plan_id"),
    symbolType: string().from("symbol_type"),
    cameraID: string().from("camera_id").optional(),
    positionLetter: string().from("position_letter").optional(),
    label: string().optional(),
    notes: string().optional(),
    point: json().optional(),
    line: json().optional(),
    followTrackID: string().from("follow_track_id").optional(),
    followFromM: number().from("follow_from_m").optional(),
    followToM: number().from("follow_to_m").optional(),
    rideRanges: json().from("ride_ranges").optional(),
    showViewAreas: boolean().from("show_view_areas"),
    sortOrder: number().from("sort_order"),
    style: json().optional(),
    visualizationID: string().from("visualization_id"),
    createdAt: number().from("created_at").optional(),
    updatedAt: number().from("updated_at").optional(),
})
    .primaryKey("id");
const cameraPlanItemViewAreas = table("cameraPlanItemViewAreas")
    .from("camera_plan_item_view_areas")
    .columns({
    id: string(),
    itemID: string().from("item_id"),
    vertices: json(),
    label: string().optional(),
    sortOrder: number().from("sort_order"),
    createdAt: number().from("created_at").optional(),
    updatedAt: number().from("updated_at").optional(),
})
    .primaryKey("id");
const cameraPlanColumns = table("cameraPlanColumns")
    .from("camera_plan_columns")
    .columns({
    id: string(),
    cameraPlanID: string().from("camera_plan_id"),
    name: string(),
    columnType: enumeration().from("column_type"),
    options: json(),
    sortOrder: number().from("sort_order").optional(),
    createdAt: number().from("created_at").optional(),
})
    .primaryKey("id");
const cameraPlanOverlays = table("cameraPlanOverlays")
    .from("camera_plan_overlays")
    .columns({
    id: string(),
    cameraPlanID: string().from("camera_plan_id"),
    overlayType: enumeration().from("overlay_type"),
    positionX: number().from("position_x"),
    positionY: number().from("position_y"),
    width: number(),
    height: number(),
    rotation: number(),
    color: string(),
    opacity: number(),
    strokeWidth: number().from("stroke_width"),
    fill: boolean(),
    imageURL: string().from("image_url").optional(),
    label: string().optional(),
    fontSize: number().from("font_size"),
    points: json().optional(),
    zIndex: number().from("z_index"),
    sortOrder: number().from("sort_order"),
    locked: boolean(),
    createdAt: number().from("created_at").optional(),
    updatedAt: number().from("updated_at").optional(),
})
    .primaryKey("id");
const cameras = table("cameras")
    .columns({
    id: string(),
    cameraPlanID: string().from("camera_plan_id"),
    number: string(),
    description: string().optional(),
    remarks: string().optional(),
    icon: string().optional(),
    positionX: number().from("position_x").optional(),
    positionY: number().from("position_y").optional(),
    rotation: number().optional(),
    track: json().optional(),
    customValues: json().from("custom_values"),
    sortOrder: number().from("sort_order").optional(),
    color: string().optional(),
    equipmentGroupID: string().from("equipment_group_id").optional(),
    profileDisplay: enumeration()
        .from("profile_display")
        .optional(),
    createdAt: number().from("created_at").optional(),
    updatedAt: number().from("updated_at").optional(),
})
    .primaryKey("id");
const cameraFunctionAssignments = table("cameraFunctionAssignments")
    .from("camera_function_assignments")
    .columns({
    id: string(),
    cameraID: string().from("camera_id"),
    functionAssignmentID: string().from("function_assignment_id"),
})
    .primaryKey("id");
export const functionAssignments = table("functionAssignments")
    .from("function_assignments")
    .columns({
    id: string(),
    functionID: string().from("function_id"),
    personID: string().from("person_id"),
    remark: string().optional(),
    createdAt: number().from("created_at").optional(),
    updatedAt: number().from("updated_at").optional(),
})
    .primaryKey("id");
export const peopleTable = table("people")
    .columns({
    id: string(),
    userID: string().from("user_id").optional(),
    companyID: string().from("company_id").optional(),
    firstName: string().from("first_name"),
    lastName: string().from("last_name"),
    number: string().optional(),
    function: string().optional(),
    phone: string().optional(),
    email: string().optional(),
    personType: enumeration().from("person_type"),
    createdAt: number().from("created_at").optional(),
    updatedAt: number().from("updated_at").optional(),
    deletedAt: number().from("deleted_at").optional(),
    version: number().optional(),
    isAdmin: boolean().from("is_admin"),
    clockifyUserID: string().from("clockify_user_id").optional(),
    rentmanID: number().from("rentman_id").optional(),
    allergies: string().optional(),
    remarks: string().optional(),
    notes: string().optional(),
    allergiesNotes: string().from("allergies_notes").optional(),
    unityUsername: string().from("unity_username").optional(),
})
    .primaryKey("id");
// ---------------------------------------------------------------------------
// SIM cards (read-only in Zero; writes go through /api/sims)
// ---------------------------------------------------------------------------
export const simCards = table("simCards")
    .from("sim_cards")
    .columns({
    id: string(),
    iccid: string(),
    provider: enumeration(),
    source: enumeration(),
    msisdn: string().optional(),
    imsi: string().optional(),
    imei: string().optional(),
    carrierAccountNbr: string().from("carrier_account_nbr").optional(),
    carrierAccountGuid: string().from("carrier_account_guid").optional(),
    status: string().optional(),
    statusLabel: string().from("status_label").optional(),
    ratePlan: string().from("rate_plan").optional(),
    commPlan: string().from("comm_plan").optional(),
    equipmentInstanceID: string().from("equipment_instance_id").optional(),
    slot: string().optional(),
    label: string().optional(),
    /** Internal note, hub only (never sent to Simetric). */
    note: string().optional(),
    simetricSegment1: string().from("simetric_segment1").optional(),
    simetricSegment2: string().from("simetric_segment2").optional(),
    simetricSegment3: string().from("simetric_segment3").optional(),
    usageCycle: number().from("usage_cycle").optional(),
    ctdDataMb: number().from("ctd_data_mb").optional(),
    ctdSms: number().from("ctd_sms").optional(),
    ctdSessions: number().from("ctd_sessions").optional(),
    usageUpdatedAt: number().from("usage_updated_at").optional(),
    simetricUpdatedAt: number().from("simetric_updated_at").optional(),
    lastSyncedAt: number().from("last_synced_at").optional(),
    missingSince: number().from("missing_since").optional(),
    profileID: string().from("profile_id").optional(),
    createdAt: number().from("created_at").optional(),
    updatedAt: number().from("updated_at").optional(),
})
    .primaryKey("id");
export const simUsageMonthly = table("simUsageMonthly")
    .from("sim_usage_monthly")
    .columns({
    simCardID: string().from("sim_card_id"),
    cycleMonth: number().from("cycle_month"),
    dataMb: number().from("data_mb"),
    sms: number(),
    voiceMin: number().from("voice_min"),
    sessions: number(),
    fetchedAt: number().from("fetched_at").optional(),
})
    .primaryKey("simCardID", "cycleMonth");
export const simProfiles = table("simProfiles")
    .from("sim_profiles")
    .columns({
    id: string(),
    provider: enumeration(),
    name: string(),
    isOpen: boolean().from("is_open"),
    ratePlan: string().from("rate_plan").optional(),
    notes: string().optional(),
})
    .primaryKey("id");
export const simProfileNetworks = table("simProfileNetworks")
    .from("sim_profile_networks")
    .columns({
    id: string(),
    profileID: string().from("profile_id"),
    countryCode: string().from("country_code"),
    networks: json(),
    note: string().optional(),
})
    .primaryKey("id");
export const imeiTacs = table("imeiTacs")
    .from("imei_tacs")
    .columns({
    tac: string(),
    brand: string(),
    name: string(),
    specs: string().optional(),
})
    .primaryKey("tac");
export const simUsageDaily = table("simUsageDaily")
    .from("sim_usage_daily")
    .columns({
    simCardID: string().from("sim_card_id"),
    day: number(),
    dataMb: number().from("data_mb"),
    isEstimated: boolean().from("is_estimated"),
    computedAt: number().from("computed_at").optional(),
})
    .primaryKey("simCardID", "day");
export const simActionBatches = table("simActionBatches")
    .from("sim_action_batches")
    .columns({
    id: string(),
    kind: enumeration(),
    requestedBy: string().from("requested_by").optional(),
    equipmentInstanceID: string().from("equipment_instance_id").optional(),
    status: enumeration(),
    summary: json(),
    createdAt: number().from("created_at").optional(),
    updatedAt: number().from("updated_at").optional(),
})
    .primaryKey("id");
export const simActionItems = table("simActionItems")
    .from("sim_action_items")
    .columns({
    id: string(),
    batchID: string().from("batch_id"),
    simCardID: string().from("sim_card_id"),
    operation: enumeration(),
    payload: json().optional(),
    state: enumeration(),
    skipReason: string().from("skip_reason").optional(),
    attempts: number(),
    lastError: string().from("last_error").optional(),
    submittedAt: number().from("submitted_at").optional(),
    finishedAt: number().from("finished_at").optional(),
    verifiedAt: number().from("verified_at").optional(),
    createdAt: number().from("created_at").optional(),
    updatedAt: number().from("updated_at").optional(),
})
    .primaryKey("id");
const groupRelationships = relationships(projectEquipmentGroups, ({ many }) => ({
    assignments: many({
        sourceField: ["id"],
        destSchema: projectEquipmentAssignments,
        destField: ["groupID"],
    }),
}));
const assignmentRelationships = relationships(projectEquipmentAssignments, ({ one, many }) => ({
    group: one({
        sourceField: ["groupID"],
        destSchema: projectEquipmentGroups,
        destField: ["id"],
    }),
    equipment: one({
        sourceField: ["equipmentID"],
        destSchema: equipment,
        destField: ["id"],
    }),
    temporaryEquipment: one({
        sourceField: ["temporaryEquipmentID"],
        destSchema: temporaryEquipment,
        destField: ["id"],
    }),
    instanceAssignments: many({
        sourceField: ["id"],
        destSchema: projectEquipmentInstanceAssignments,
        destField: ["assignmentID"],
    }),
}));
const instanceAssignmentRelationships = relationships(projectEquipmentInstanceAssignments, ({ one }) => ({
    instance: one({
        sourceField: ["instanceID"],
        destSchema: equipmentInstances,
        destField: ["id"],
    }),
}));
const equipmentRelationships = relationships(equipment, ({ one, many }) => ({
    prices: one({
        sourceField: ["id"],
        destSchema: equipmentPrices,
        destField: ["equipmentID"],
    }),
    tagAssignments: many({
        sourceField: ["id"],
        destSchema: equipmentTagAssignments,
        destField: ["equipmentID"],
    }),
    images: many({
        sourceField: ["id"],
        destSchema: equipmentImages,
        destField: ["equipmentID"],
    }),
    contents: many({
        sourceField: ["id"],
        destSchema: equipmentContents,
        destField: ["parentEquipmentID"],
    }),
}));
const equipmentContentRelationships = relationships(equipmentContents, ({ one }) => ({
    childEquipment: one({
        sourceField: ["childEquipmentID"],
        destSchema: equipment,
        destField: ["id"],
    }),
}));
const equipmentTagAssignmentRelationships = relationships(equipmentTagAssignments, ({ one }) => ({
    tag: one({
        sourceField: ["tagID"],
        destSchema: equipmentTags,
        destField: ["id"],
    }),
}));
const equipmentTagRelationships = relationships(equipmentTags, ({ many }) => ({
    assignments: many({
        sourceField: ["id"],
        destSchema: equipmentTagAssignments,
        destField: ["tagID"],
    }),
}));
const cameraPlanRelationships = relationships(cameraPlans, ({ many }) => ({
    columns: many({
        sourceField: ["id"],
        destSchema: cameraPlanColumns,
        destField: ["cameraPlanID"],
    }),
    cameras: many({
        sourceField: ["id"],
        destSchema: cameras,
        destField: ["cameraPlanID"],
    }),
    overlays: many({
        sourceField: ["id"],
        destSchema: cameraPlanOverlays,
        destField: ["cameraPlanID"],
    }),
    tracks: many({
        sourceField: ["id"],
        destSchema: cameraPlanTracks,
        destField: ["cameraPlanID"],
    }),
    items: many({
        sourceField: ["id"],
        destSchema: cameraPlanItems,
        destField: ["cameraPlanID"],
    }),
    visualizations: many({
        sourceField: ["id"],
        destSchema: cameraPlanVisualizations,
        destField: ["cameraPlanID"],
    }),
}));
const cameraPlanVisualizationRelationships = relationships(cameraPlanVisualizations, ({ one }) => ({
    cameraPlan: one({
        sourceField: ["cameraPlanID"],
        destSchema: cameraPlans,
        destField: ["id"],
    }),
}));
const cameraPlanItemRelationships = relationships(cameraPlanItems, ({ one, many }) => ({
    camera: one({
        sourceField: ["cameraID"],
        destSchema: cameras,
        destField: ["id"],
    }),
    viewAreas: many({
        sourceField: ["id"],
        destSchema: cameraPlanItemViewAreas,
        destField: ["itemID"],
    }),
}));
const cameraPlanColumnRelationships = relationships(cameraPlanColumns, ({ one }) => ({
    cameraPlan: one({
        sourceField: ["cameraPlanID"],
        destSchema: cameraPlans,
        destField: ["id"],
    }),
}));
const cameraPlanOverlayRelationships = relationships(cameraPlanOverlays, ({ one }) => ({
    cameraPlan: one({
        sourceField: ["cameraPlanID"],
        destSchema: cameraPlans,
        destField: ["id"],
    }),
}));
const cameraRelationships = relationships(cameras, ({ one, many }) => ({
    cameraPlan: one({
        sourceField: ["cameraPlanID"],
        destSchema: cameraPlans,
        destField: ["id"],
    }),
    equipmentGroup: one({
        sourceField: ["equipmentGroupID"],
        destSchema: projectEquipmentGroups,
        destField: ["id"],
    }),
    cameraFunctionAssignments: many({
        sourceField: ["id"],
        destSchema: cameraFunctionAssignments,
        destField: ["cameraID"],
    }),
    items: many({
        sourceField: ["id"],
        destSchema: cameraPlanItems,
        destField: ["cameraID"],
    }),
}));
const cameraFunctionAssignmentRelationships = relationships(cameraFunctionAssignments, ({ one }) => ({
    functionAssignment: one({
        sourceField: ["functionAssignmentID"],
        destSchema: functionAssignments,
        destField: ["id"],
    }),
}));
export const functionAssignmentRelationships = relationships(functionAssignments, ({ one }) => ({
    person: one({
        sourceField: ["personID"],
        destSchema: peopleTable,
        destField: ["id"],
    }),
}));
const equipmentInstanceRelationships = relationships(equipmentInstances, ({ one, many }) => ({
    equipment: one({
        sourceField: ["equipmentID"],
        destSchema: equipment,
        destField: ["id"],
    }),
    prices: one({
        sourceField: ["id"],
        destSchema: equipmentInstancePrices,
        destField: ["instanceID"],
    }),
    simCards: many({
        sourceField: ["id"],
        destSchema: simCards,
        destField: ["equipmentInstanceID"],
    }),
}));
const simCardRelationships = relationships(simCards, ({ one, many }) => ({
    instance: one({
        sourceField: ["equipmentInstanceID"],
        destSchema: equipmentInstances,
        destField: ["id"],
    }),
    usageMonthly: many({
        sourceField: ["id"],
        destSchema: simUsageMonthly,
        destField: ["simCardID"],
    }),
    usageDaily: many({
        sourceField: ["id"],
        destSchema: simUsageDaily,
        destField: ["simCardID"],
    }),
    actionItems: many({
        sourceField: ["id"],
        destSchema: simActionItems,
        destField: ["simCardID"],
    }),
}));
const simProfileRelationships = relationships(simProfiles, ({ many }) => ({
    networks: many({
        sourceField: ["id"],
        destSchema: simProfileNetworks,
        destField: ["profileID"],
    }),
}));
const simActionBatchRelationships = relationships(simActionBatches, ({ one, many }) => ({
    items: many({
        sourceField: ["id"],
        destSchema: simActionItems,
        destField: ["batchID"],
    }),
    requester: one({
        sourceField: ["requestedBy"],
        destSchema: peopleTable,
        destField: ["id"],
    }),
    instance: one({
        sourceField: ["equipmentInstanceID"],
        destSchema: equipmentInstances,
        destField: ["id"],
    }),
}));
const simActionItemRelationships = relationships(simActionItems, ({ one }) => ({
    simCard: one({
        sourceField: ["simCardID"],
        destSchema: simCards,
        destField: ["id"],
    }),
    batch: one({
        sourceField: ["batchID"],
        destSchema: simActionBatches,
        destField: ["id"],
    }),
}));
export const equipmentPlanTables = [
    projectEquipmentGroups,
    projectEquipmentAssignments,
    projectEquipmentInstanceAssignments,
    temporaryEquipment,
    equipment,
    equipmentPrices,
    equipmentInstances,
    equipmentInstancePrices,
    equipmentTagAssignments,
    equipmentTags,
    equipmentImages,
    equipmentContents,
    cameraPlans,
    cameraPlanVisualizations,
    cameraPlanColumns,
    cameraPlanOverlays,
    cameraPlanTracks,
    cameraPlanItems,
    cameraPlanItemViewAreas,
    cameras,
    cameraFunctionAssignments,
    functionAssignments,
    peopleTable,
    simCards,
    simUsageMonthly,
    simUsageDaily,
    simActionBatches,
    simActionItems,
    imeiTacs,
    simProfiles,
    simProfileNetworks,
];
export const equipmentPlanRelationships = [
    groupRelationships,
    assignmentRelationships,
    instanceAssignmentRelationships,
    equipmentRelationships,
    equipmentTagAssignmentRelationships,
    equipmentTagRelationships,
    equipmentContentRelationships,
    cameraPlanRelationships,
    cameraPlanItemRelationships,
    cameraPlanVisualizationRelationships,
    cameraPlanColumnRelationships,
    cameraPlanOverlayRelationships,
    cameraRelationships,
    cameraFunctionAssignmentRelationships,
    functionAssignmentRelationships,
    equipmentInstanceRelationships,
    simCardRelationships,
    simProfileRelationships,
    simActionBatchRelationships,
    simActionItemRelationships,
];
export const equipmentPlanSchema = createSchema({
    tables: equipmentPlanTables,
    relationships: equipmentPlanRelationships,
});
export const equipmentPlanZql = createBuilder(equipmentPlanSchema);
const defineEquipmentQuery = defineQueryWithMeta();
// Query args mirror what the web app sends today. IDs stay plain strings:
// call sites pass placeholders ("__none__", the zero UUID) while a lookup is
// not ready yet.
const equipmentIDQueryArgsSchema = z.object({ id: z.string() });
const byEquipmentQueryArgsSchema = z.object({ equipmentID: z.string() });
const byEquipmentIDsQueryArgsSchema = z.object({
    equipmentIDs: z.array(z.string()),
});
const byParentEquipmentQueryArgsSchema = z.object({
    parentEquipmentID: z
        .string()
        .describe("ID of the container equipment whose contents to return"),
});
const equipmentProjectIDQueryArgsSchema = z.object({ projectID: z.string() });
const cameraPlanIDQueryArgsSchema = z.object({ cameraPlanID: z.string() });
const simInstanceQueryArgsSchema = z.object({
    instanceID: z.string().describe("Equipment instance (device) ID"),
});
const simInstancesQueryArgsSchema = z.object({
    instanceIDs: z.array(z.string()).describe("Equipment instance (device) IDs"),
});
const simEligibleDevicesQueryArgsSchema = z.object({
    tagIDs: z
        .array(z.string())
        .describe("Equipment tag IDs; returns instances of non-deleted equipment carrying any of them"),
});
const simUsageMonthlyQueryArgsSchema = z.object({
    since: z
        .number()
        .describe("Only billing months starting at or after this time (epoch ms)"),
});
const imeiModelsQueryArgsSchema = z.object({
    tacs: z
        .array(z.string())
        .describe("Type Allocation Codes (first 8 digits of an IMEI)"),
});
const simCardIDsQueryArgsSchema = z.object({
    simCardIDs: z.array(z.string()),
});
const simUsageDailyQueryArgsSchema = z.object({
    simCardIDs: z.array(z.string()),
    since: z.number().describe("Only days at or after this time (epoch ms)"),
});
const simRecentBatchesQueryArgsSchema = z.object({
    limit: z
        .number()
        .describe("Maximum number of batches to return (clamped to 1-50)"),
});
/** Restricts a child table to readable projects through its parent row. */
function scopeViaParent(ctx, query, relationship) {
    if (isStaff(ctx))
        return query;
    return query.whereExists(relationship, (parent) => scopeToReadableProjects(ctx, parent));
}
export const equipmentPlanQueryDefinitions = {
    equipmentTags: {
        list: defineEquipmentQuery(noArgsSchema, ({ ctx }) => {
            if (ctx.role !== "admin" && ctx.role !== "employee") {
                return equipmentPlanZql.equipmentTags.where("id", "__unauthorized__");
            }
            return equipmentPlanZql.equipmentTags.orderBy("name", "asc");
        }),
        counts: defineEquipmentQuery(noArgsSchema, ({ ctx }) => {
            if (ctx.role !== "admin" && ctx.role !== "employee") {
                return equipmentPlanZql.equipmentTagAssignments.where("id", "__unauthorized__");
            }
            return equipmentPlanZql.equipmentTagAssignments;
        }),
    },
    equipmentCatalog: {
        list: defineEquipmentQuery(noArgsSchema, ({ ctx }) => {
            if (ctx.role !== "admin" && ctx.role !== "employee") {
                return equipmentPlanZql.equipment.where("id", "__unauthorized__");
            }
            return equipmentPlanZql.equipment
                .where("deletedAt", "IS", null)
                .related("prices")
                .related("tagAssignments", (assignment) => assignment.related("tag"))
                .related("images")
                .orderBy("name", "asc");
        }),
        byID: defineEquipmentQuery(equipmentIDQueryArgsSchema, ({ args, ctx }) => {
            if (ctx.role !== "admin" && ctx.role !== "employee") {
                return equipmentPlanZql.equipment
                    .where("id", "__unauthorized__")
                    .one();
            }
            return equipmentPlanZql.equipment
                .where("id", args.id)
                .related("prices")
                .related("tagAssignments", (assignment) => assignment.related("tag"))
                .related("images")
                .one();
        }),
    },
    equipmentInstances: {
        byEquipment: defineEquipmentQuery(byEquipmentQueryArgsSchema, ({ args, ctx, }) => {
            if (ctx.role !== "admin" && ctx.role !== "employee") {
                return equipmentPlanZql.equipmentInstances.where("id", "__unauthorized__");
            }
            return equipmentPlanZql.equipmentInstances
                .where("equipmentID", args.equipmentID)
                .related("prices")
                .orderBy("identifier", "asc");
        }),
        byEquipmentIDs: defineEquipmentQuery(byEquipmentIDsQueryArgsSchema, ({ args, ctx, }) => {
            if (ctx.role !== "admin" && ctx.role !== "employee") {
                return equipmentPlanZql.equipmentInstances.where("id", "__unauthorized__");
            }
            return equipmentPlanZql.equipmentInstances
                .where("equipmentID", "IN", args.equipmentIDs)
                .related("prices")
                .orderBy("identifier", "asc");
        }),
    },
    equipmentContents: {
        byParent: defineEquipmentQuery(byParentEquipmentQueryArgsSchema, ({ args, ctx, }) => {
            if (ctx.role !== "admin" && ctx.role !== "employee") {
                return equipmentPlanZql.equipmentContents.where("id", "__unauthorized__");
            }
            return equipmentPlanZql.equipmentContents
                .where("parentEquipmentID", args.parentEquipmentID)
                .related("childEquipment")
                .orderBy("createdAt", "asc");
        }),
    },
    equipmentConflicts: {
        assignmentsByEquipment: defineEquipmentQuery(byEquipmentIDsQueryArgsSchema, ({ args, ctx, }) => {
            if (ctx.role !== "admin" && ctx.role !== "employee") {
                return equipmentPlanZql.projectEquipmentAssignments.where("id", "__unauthorized__");
            }
            return equipmentPlanZql.projectEquipmentAssignments
                .where("equipmentID", "IN", args.equipmentIDs)
                .related("group")
                .related("instanceAssignments", (instanceAssignment) => instanceAssignment.related("instance"))
                .orderBy("createdAt", "asc");
        }),
    },
    equipmentPlan: {
        byProject: defineEquipmentQuery(equipmentProjectIDQueryArgsSchema, ({ args, ctx, }) => {
            if (!canReadProject(ctx, args.projectID)) {
                return equipmentPlanZql.projectEquipmentGroups.where("id", "__unauthorized__");
            }
            // Prices are staff-only: freelancer collaborators read this plan too
            const staff = isStaff(ctx);
            return equipmentPlanZql.projectEquipmentGroups
                .where("projectID", args.projectID)
                .related("assignments", (assignment) => assignment
                .related("equipment", (eq) => staff
                ? eq.related("tagAssignments").related("prices")
                : eq.related("tagAssignments"))
                .related("temporaryEquipment")
                .related("instanceAssignments", (instanceAssignment) => instanceAssignment.related("instance")))
                .orderBy("displayOrder", "asc")
                .orderBy("name", "asc");
        }),
    },
    cameraPlans: {
        byProject: defineEquipmentQuery(equipmentProjectIDQueryArgsSchema, ({ args, ctx, }) => {
            if (!canReadProject(ctx, args.projectID)) {
                return equipmentPlanZql.cameraPlans.where("id", "__unauthorized__");
            }
            return equipmentPlanZql.cameraPlans
                .where("projectID", args.projectID)
                .related("visualizations", (v) => v.orderBy("sortOrder", "asc"))
                .orderBy("sortOrder", "asc")
                .orderBy("createdAt", "asc");
        }),
        withCamerasByProject: defineEquipmentQuery(equipmentProjectIDQueryArgsSchema, ({ args, ctx, }) => {
            if (!canReadProject(ctx, args.projectID)) {
                return equipmentPlanZql.cameraPlans.where("id", "__unauthorized__");
            }
            return equipmentPlanZql.cameraPlans
                .where("projectID", args.projectID)
                .related("cameras", (camera) => camera
                .related("cameraFunctionAssignments", (cameraFunctionAssignment) => cameraFunctionAssignment.related("functionAssignment", (fa) => fa.related("person")))
                .orderBy("number", "asc"))
                .orderBy("sortOrder", "asc")
                .orderBy("createdAt", "asc");
        }),
        columnsByPlan: defineEquipmentQuery(cameraPlanIDQueryArgsSchema, ({ args, ctx, }) => {
            return scopeViaParent(ctx, equipmentPlanZql.cameraPlanColumns.where("cameraPlanID", args.cameraPlanID), "cameraPlan")
                .orderBy("sortOrder", "asc")
                .orderBy("createdAt", "asc");
        }),
        visualizationsByPlan: defineEquipmentQuery(cameraPlanIDQueryArgsSchema, ({ args, ctx, }) => {
            return scopeViaParent(ctx, equipmentPlanZql.cameraPlanVisualizations.where("cameraPlanID", args.cameraPlanID), "cameraPlan")
                .orderBy("sortOrder", "asc")
                .orderBy("createdAt", "asc");
        }),
        overlaysByPlan: defineEquipmentQuery(cameraPlanIDQueryArgsSchema, ({ args, ctx, }) => {
            return scopeViaParent(ctx, equipmentPlanZql.cameraPlanOverlays.where("cameraPlanID", args.cameraPlanID), "cameraPlan")
                .orderBy("zIndex", "asc")
                .orderBy("sortOrder", "asc");
        }),
        camerasByPlan: defineEquipmentQuery(cameraPlanIDQueryArgsSchema, ({ args, ctx, }) => {
            return scopeViaParent(ctx, equipmentPlanZql.cameras.where("cameraPlanID", args.cameraPlanID), "cameraPlan")
                .related("equipmentGroup")
                .related("cameraFunctionAssignments", (cameraFunctionAssignment) => cameraFunctionAssignment.related("functionAssignment", (fa) => fa.related("person")))
                .orderBy("sortOrder", "asc")
                .orderBy("createdAt", "asc");
        }),
        tracksByPlan: defineEquipmentQuery(cameraPlanIDQueryArgsSchema, ({ args, ctx, }) => {
            if (ctx.role !== "admin" && ctx.role !== "employee") {
                return equipmentPlanZql.cameraPlanTracks.where("id", "__unauthorized__");
            }
            return equipmentPlanZql.cameraPlanTracks
                .where("cameraPlanID", args.cameraPlanID)
                .orderBy("sortOrder", "asc")
                .orderBy("createdAt", "asc");
        }),
        itemsByPlan: defineEquipmentQuery(cameraPlanIDQueryArgsSchema, ({ args, ctx, }) => {
            if (ctx.role !== "admin" && ctx.role !== "employee") {
                return equipmentPlanZql.cameraPlanItems.where("id", "__unauthorized__");
            }
            return equipmentPlanZql.cameraPlanItems
                .where("cameraPlanID", args.cameraPlanID)
                .related("viewAreas", (area) => area.orderBy("sortOrder", "asc"))
                .orderBy("sortOrder", "asc")
                .orderBy("createdAt", "asc");
        }),
        camerasByProject: defineEquipmentQuery(equipmentProjectIDQueryArgsSchema, ({ args, ctx, }) => {
            if (!canReadProject(ctx, args.projectID)) {
                return equipmentPlanZql.cameras.where("id", "__unauthorized__");
            }
            return equipmentPlanZql.cameras
                .whereExists("cameraPlan", (plan) => plan.where("projectID", args.projectID))
                .related("cameraPlan")
                .related("cameraFunctionAssignments", (cameraFunctionAssignment) => cameraFunctionAssignment.related("functionAssignment", (fa) => fa.related("person")))
                .orderBy("number", "asc");
        }),
    },
    sims: {
        list: defineEquipmentQuery(noArgsSchema, ({ ctx }) => {
            if (ctx.role !== "admin" && ctx.role !== "employee") {
                return equipmentPlanZql.simCards.where("id", "__unauthorized__");
            }
            return equipmentPlanZql.simCards
                .related("instance", (instance) => instance.related("equipment"))
                .orderBy("iccid", "asc");
        }),
        byId: defineEquipmentQuery(equipmentIDQueryArgsSchema, ({ args, ctx }) => {
            if (ctx.role !== "admin" && ctx.role !== "employee") {
                return equipmentPlanZql.simCards
                    .where("id", "__unauthorized__")
                    .one();
            }
            return equipmentPlanZql.simCards
                .where("id", args.id)
                .related("instance", (instance) => instance.related("equipment"))
                .one();
        }),
        byInstance: defineEquipmentQuery(simInstanceQueryArgsSchema, ({ args, ctx, }) => {
            if (ctx.role !== "admin" && ctx.role !== "employee") {
                return equipmentPlanZql.simCards.where("id", "__unauthorized__");
            }
            return equipmentPlanZql.simCards
                .where("equipmentInstanceID", args.instanceID)
                .related("instance", (instance) => instance.related("equipment"))
                .orderBy("slot", "asc")
                .orderBy("iccid", "asc");
        }),
        byInstances: defineEquipmentQuery(simInstancesQueryArgsSchema, ({ args, ctx, }) => {
            if (ctx.role !== "admin" && ctx.role !== "employee") {
                return equipmentPlanZql.simCards.where("id", "__unauthorized__");
            }
            return equipmentPlanZql.simCards
                .where("equipmentInstanceID", "IN", args.instanceIDs)
                .orderBy("slot", "asc")
                .orderBy("iccid", "asc");
        }),
        eligibleDevices: defineEquipmentQuery(simEligibleDevicesQueryArgsSchema, ({ args, ctx, }) => {
            if ((ctx.role !== "admin" && ctx.role !== "employee") ||
                args.tagIDs.length === 0) {
                return equipmentPlanZql.equipmentInstances.where("id", "__unauthorized__");
            }
            return equipmentPlanZql.equipmentInstances
                .whereExists("equipment", (eq) => eq
                .where("deletedAt", "IS", null)
                .whereExists("tagAssignments", (assignment) => assignment.where("tagID", "IN", args.tagIDs)))
                .related("equipment")
                .orderBy("identifier", "asc");
        }),
        usageMonthly: defineEquipmentQuery(simUsageMonthlyQueryArgsSchema, ({ args, ctx, }) => {
            if (ctx.role !== "admin" && ctx.role !== "employee") {
                return equipmentPlanZql.simUsageMonthly.where("simCardID", "__unauthorized__");
            }
            return equipmentPlanZql.simUsageMonthly
                .where("cycleMonth", ">=", args.since)
                .orderBy("cycleMonth", "asc");
        }),
        profiles: defineEquipmentQuery(noArgsSchema, ({ ctx }) => {
            if (ctx.role !== "admin" && ctx.role !== "employee") {
                return equipmentPlanZql.simProfiles.where("id", "__unauthorized__");
            }
            return equipmentPlanZql.simProfiles
                .related("networks", (network) => network.orderBy("countryCode", "asc"))
                .orderBy("provider", "asc")
                .orderBy("name", "asc");
        }),
        imeiModels: defineEquipmentQuery(imeiModelsQueryArgsSchema, ({ args, ctx, }) => {
            if (ctx.role !== "admin" && ctx.role !== "employee") {
                return equipmentPlanZql.imeiTacs.where("tac", "__unauthorized__");
            }
            return equipmentPlanZql.imeiTacs.where("tac", "IN", args.tacs);
        }),
        usageMonthlyBySims: defineEquipmentQuery(simCardIDsQueryArgsSchema, ({ args, ctx, }) => {
            if (ctx.role !== "admin" && ctx.role !== "employee") {
                return equipmentPlanZql.simUsageMonthly.where("simCardID", "__unauthorized__");
            }
            return equipmentPlanZql.simUsageMonthly
                .where("simCardID", "IN", args.simCardIDs)
                .orderBy("cycleMonth", "asc");
        }),
        usageDailyBySims: defineEquipmentQuery(simUsageDailyQueryArgsSchema, ({ args, ctx, }) => {
            if (ctx.role !== "admin" && ctx.role !== "employee") {
                return equipmentPlanZql.simUsageDaily.where("simCardID", "__unauthorized__");
            }
            return equipmentPlanZql.simUsageDaily
                .where("simCardID", "IN", args.simCardIDs)
                .where("day", ">=", args.since)
                .orderBy("day", "asc");
        }),
        recentBatches: defineEquipmentQuery(simRecentBatchesQueryArgsSchema, ({ args, ctx, }) => {
            if (ctx.role !== "admin" && ctx.role !== "employee") {
                return equipmentPlanZql.simActionBatches.where("id", "__unauthorized__");
            }
            return equipmentPlanZql.simActionBatches
                .related("items", (item) => item.related("simCard"))
                .related("requester")
                .related("instance", (instance) => instance.related("equipment"))
                .orderBy("createdAt", "desc")
                .limit(Math.min(Math.max(args.limit, 1), 50));
        }),
        openItems: defineEquipmentQuery(noArgsSchema, ({ ctx }) => {
            if (ctx.role !== "admin" && ctx.role !== "employee") {
                return equipmentPlanZql.simActionItems.where("id", "__unauthorized__");
            }
            return equipmentPlanZql.simActionItems.where("state", "IN", [
                "queued",
                "submitted",
            ]);
        }),
    },
};
export const equipmentPlanQueries = defineQueries(equipmentPlanQueryDefinitions);
const defineEquipmentMutator = defineMutatorWithMeta();
const uuidSchema = z.string().uuid();
const trimmedNameSchema = z.string().trim().min(1).max(500);
const nullableUUIDSchema = uuidSchema.nullable().optional();
const nullableStringSchema = z.string().nullable().optional();
const nullableNumberSchema = z.number().nullable().optional();
const assignmentEquipmentRefsShape = {
    equipmentID: nullableUUIDSchema,
    temporaryEquipmentID: nullableUUIDSchema,
};
const equipmentTagIconSchema = z.string().trim().max(64).nullable().optional();
export const createEquipmentTagInputSchema = z.object({
    id: uuidSchema,
    name: trimmedNameSchema,
    parentID: nullableUUIDSchema,
    icon: equipmentTagIconSchema,
});
export const updateEquipmentTagInputSchema = z
    .object({
    id: uuidSchema,
    name: trimmedNameSchema.optional(),
    parentID: nullableUUIDSchema,
    icon: equipmentTagIconSchema,
})
    .refine(({ id: _id, ...updates }) => Object.values(updates).some((value) => value !== undefined), "At least one equipment tag field must be updated");
export const equipmentTagIDInputSchema = z.object({
    id: uuidSchema,
});
const equipmentCatalogInputShape = {
    name: trimmedNameSchema,
    number: nullableStringSchema,
    manufacturer: nullableStringSchema,
    icon: nullableStringSchema,
    newPrice: nullableNumberSchema,
    rentalPrice: nullableNumberSchema,
    isSerialized: z.boolean(),
    stockQuantity: z.number().int().min(0).nullable().optional(),
    notes: nullableStringSchema,
    parentEquipmentID: nullableUUIDSchema,
    rentmanID: z.number().int().nullable().optional(),
    weight: nullableNumberSchema,
    width: nullableNumberSchema,
    height: nullableNumberSchema,
    length: nullableNumberSchema,
    volume: nullableNumberSchema,
    power: nullableNumberSchema,
    currentDraw: nullableNumberSchema,
    qrCode: nullableStringSchema,
    externalRemark: nullableStringSchema,
    locationInWarehouse: nullableStringSchema,
    shortName: nullableStringSchema,
    countryOfOrigin: nullableStringSchema,
    customsTariffNumber: nullableStringSchema,
    carnetAtaDescription: nullableStringSchema,
    carnetAtaValue: nullableNumberSchema,
    carnetAtaWeightUnit: nullableStringSchema,
};
export const createEquipmentCatalogInputSchema = z.object({
    id: uuidSchema,
    ...equipmentCatalogInputShape,
    tagIDs: z.array(uuidSchema).optional(),
});
export const updateEquipmentCatalogInputSchema = z
    .object({
    id: uuidSchema,
    ...Object.fromEntries(Object.entries(equipmentCatalogInputShape).map(([key, schema]) => [
        key,
        schema.optional(),
    ])),
    tagIDs: z.array(uuidSchema).optional(),
})
    .refine(({ id: _id, ...updates }) => Object.values(updates).some((value) => value !== undefined), "At least one equipment field must be updated");
export const softDeleteEquipmentCatalogInputSchema = z.object({
    id: uuidSchema,
});
const equipmentInstanceStatusSchema = z.enum(["active", "repair", "archived"]);
export const createEquipmentInstanceInputSchema = z.object({
    id: uuidSchema,
    equipmentID: uuidSchema,
    identifier: trimmedNameSchema,
    notes: nullableStringSchema,
    acquisitionDate: nullableStringSchema,
    acquisitionPrice: nullableNumberSchema,
    vendorID: nullableUUIDSchema,
    status: equipmentInstanceStatusSchema.optional(),
    qrCode: nullableStringSchema,
    serialNumber: nullableStringSchema,
});
export const updateEquipmentInstanceInputSchema = z
    .object({
    id: uuidSchema,
    identifier: trimmedNameSchema.optional(),
    notes: nullableStringSchema,
    acquisitionDate: nullableStringSchema,
    acquisitionPrice: nullableNumberSchema,
    vendorID: nullableUUIDSchema,
    status: equipmentInstanceStatusSchema.optional(),
    qrCode: nullableStringSchema,
    serialNumber: nullableStringSchema,
})
    .refine(({ id: _id, ...updates }) => Object.values(updates).some((value) => value !== undefined), "At least one equipment instance field must be updated");
export const deleteEquipmentInstanceInputSchema = z.object({
    id: uuidSchema,
});
export const createEquipmentImageInputSchema = z.object({
    id: uuidSchema,
    equipmentID: uuidSchema,
    imageURL: z.string().url(),
    displayOrder: z.number().int().min(0).nullable().optional(),
});
export const deleteEquipmentImageInputSchema = z.object({
    id: uuidSchema,
});
export const addEquipmentContentInputSchema = z
    .object({
    id: uuidSchema,
    parentEquipmentID: uuidSchema,
    /** Stock article; omit for a free line */
    childEquipmentID: uuidSchema.optional(),
    /** Name of a free line; omit for a stock article */
    name: z.string().trim().min(1).max(500).optional(),
    quantity: z.number().int().min(1).max(9999).optional(),
})
    .refine((args) => (args.childEquipmentID ? !args.name : !!args.name), {
    message: "Provide either childEquipmentID or name",
});
export const updateEquipmentContentNameInputSchema = z.object({
    id: uuidSchema,
    name: z.string().trim().min(1).max(500),
});
export const updateEquipmentContentQuantityInputSchema = z.object({
    id: uuidSchema,
    quantity: z.number().int().min(1).max(9999),
});
export const removeEquipmentContentInputSchema = z.object({
    id: uuidSchema,
});
export const createEquipmentGroupInputSchema = z.object({
    id: uuidSchema,
    projectID: uuidSchema,
    name: trimmedNameSchema,
    displayOrder: z.number().int().min(0).nullable().optional(),
});
export const updateEquipmentGroupInputSchema = z
    .object({
    id: uuidSchema,
    name: trimmedNameSchema.optional(),
    packingState: z.enum(["open", "packed"]).optional(),
    riggingState: z.enum(["open", "in_progress", "ready", "tested"]).optional(),
    displayOrder: z.number().int().min(0).nullable().optional(),
    isCollapsed: z.boolean().nullable().optional(),
})
    .refine(({ id: _id, ...updates }) => Object.values(updates).some((value) => value !== undefined), "At least one group field must be updated");
export const deleteEquipmentGroupInputSchema = z.object({
    id: uuidSchema,
});
export const deleteEquipmentAssignmentInputSchema = z.object({
    id: uuidSchema,
});
export const createTemporaryEquipmentInputSchema = z.object({
    id: uuidSchema,
    projectID: uuidSchema,
    name: trimmedNameSchema,
    countryOfOrigin: nullableStringSchema,
    carnetAtaDescription: nullableStringSchema,
    carnetAtaValue: nullableNumberSchema,
    weight: nullableNumberSchema,
    carnetAtaWeightUnit: nullableStringSchema,
});
export const updateTemporaryEquipmentInputSchema = z
    .object({
    id: uuidSchema,
    name: trimmedNameSchema.optional(),
    countryOfOrigin: nullableStringSchema,
    carnetAtaDescription: nullableStringSchema,
    carnetAtaValue: nullableNumberSchema,
    weight: nullableNumberSchema,
    carnetAtaWeightUnit: nullableStringSchema,
})
    .refine(({ id: _id, ...updates }) => Object.values(updates).some((value) => value !== undefined), "At least one temporary equipment field must be updated");
export const createEquipmentAssignmentInputSchema = z
    .object({
    ...assignmentEquipmentRefsShape,
    id: uuidSchema,
    groupID: uuidSchema,
    amount: z.number().int().min(1).max(9999).optional(),
    notes: z.string().max(5000).nullable().optional(),
    cameraPlanColumnKey: z
        .string()
        .trim()
        .min(1)
        .max(100)
        .nullable()
        .optional(),
})
    .refine((args) => !(args.equipmentID && args.temporaryEquipmentID), "Use either equipmentID or temporaryEquipmentID, not both");
export const updateEquipmentAssignmentInputSchema = z
    .object({
    ...assignmentEquipmentRefsShape,
    id: uuidSchema,
    amount: z.number().int().min(1).max(9999).optional(),
    notes: z.string().max(5000).nullable().optional(),
    cameraPlanColumnKey: z
        .string()
        .trim()
        .min(1)
        .max(100)
        .nullable()
        .optional(),
})
    .refine((args) => !(args.equipmentID && args.temporaryEquipmentID), "Use either equipmentID or temporaryEquipmentID, not both")
    .refine(({ id: _id, ...updates }) => Object.values(updates).some((value) => value !== undefined), "At least one assignment field must be updated");
export const assignEquipmentInstanceInputSchema = z.object({
    id: uuidSchema,
    assignmentID: uuidSchema,
    instanceID: uuidSchema,
});
export const unassignEquipmentInstanceInputSchema = z.object({
    id: uuidSchema,
});
const visualizationTypeSchema = z
    .enum(["geo", "image", "embed"])
    .describe("geo: a map; image: an uploaded image; embed: an embedded page");
const viewDefaultsSchema = z
    .record(z.string(), z.any())
    .describe("Default view state of a map or image (base map, layers, viewport)");
const cameraPlanColumnTypeSchema = z.enum(["text", "select", "checkbox"]);
const cameraCustomValueSchema = z.union([
    z.string(),
    z.boolean(),
    z.array(z.string()),
]);
const cameraPlanOverlayTypeSchema = z.enum([
    "image",
    "rectangle",
    "ellipse",
    "line",
    "text",
]);
export function toZeroDate(value) {
    if (value === null)
        return null;
    if (value === undefined)
        return undefined;
    const [year, month, day] = value.split("-").map(Number);
    if (!year || !month || !day)
        return null;
    return Date.UTC(year, month - 1, day);
}
export const createCameraPlanInputSchema = z.object({
    id: uuidSchema,
    projectID: uuidSchema,
    name: trimmedNameSchema,
    description: nullableStringSchema,
    eventDate: nullableStringSchema,
    sortOrder: z.number().int().min(0).optional(),
    subtitle: nullableStringSchema,
    visualization: z
        .object({
        id: uuidSchema,
        name: trimmedNameSchema,
        vizType: visualizationTypeSchema,
        embedURL: nullableStringSchema,
        viewDefaults: viewDefaultsSchema.optional(),
    })
        .optional()
        .describe("The plan's first visualization"),
});
const imageAspectSchema = z
    .number()
    .min(0.05)
    .max(20)
    .describe("Height / width of the image");
export const createCameraPlanVisualizationInputSchema = z.object({
    id: uuidSchema,
    cameraPlanID: uuidSchema,
    name: trimmedNameSchema,
    vizType: visualizationTypeSchema,
    imageURL: nullableStringSchema,
    imageAspect: imageAspectSchema.nullable().optional(),
    embedURL: nullableStringSchema,
    viewDefaults: viewDefaultsSchema.optional(),
    sortOrder: z.number().int().min(0).optional(),
});
export const updateCameraPlanVisualizationInputSchema = z
    .object({
    id: uuidSchema,
    name: trimmedNameSchema.optional(),
    imageURL: nullableStringSchema,
    imageAspect: imageAspectSchema.nullable().optional(),
    embedURL: nullableStringSchema,
    viewDefaults: viewDefaultsSchema.optional(),
    sortOrder: z.number().int().min(0).optional(),
})
    .refine(({ id: _id, ...updates }) => Object.values(updates).some((value) => value !== undefined), "At least one visualization field must be updated");
export const adoptImageVisualizationInputSchema = z.object({
    id: uuidSchema,
    imageAspect: imageAspectSchema,
});
export const updateCameraPlanInputSchema = z
    .object({
    id: uuidSchema,
    name: trimmedNameSchema.optional(),
    description: nullableStringSchema,
    eventDate: nullableStringSchema,
    sortOrder: z.number().int().min(0).optional(),
    subtitle: nullableStringSchema,
    markSnapDistanceM: z
        .number()
        .int()
        .min(1)
        .max(5000)
        .optional()
        .describe("A position counts as on a track when at most this far away"),
    markMergeDistanceM: z
        .number()
        .int()
        .min(0)
        .max(50000)
        .optional()
        .describe("Passes on one track closer than this along it merge"),
})
    .refine(({ id: _id, ...updates }) => Object.values(updates).some((value) => value !== undefined), "At least one camera plan field must be updated");
export const deleteCameraPlanInputSchema = z.object({
    id: uuidSchema,
});
export const createCameraPlanColumnInputSchema = z.object({
    id: uuidSchema,
    cameraPlanID: uuidSchema,
    name: trimmedNameSchema,
    columnType: cameraPlanColumnTypeSchema,
    options: z.array(z.string()).optional(),
    sortOrder: z.number().int().min(0).optional(),
});
export const updateCameraPlanColumnInputSchema = z
    .object({
    id: uuidSchema,
    name: trimmedNameSchema.optional(),
    columnType: cameraPlanColumnTypeSchema.optional(),
    options: z.array(z.string()).optional(),
    sortOrder: z.number().int().min(0).optional(),
})
    .refine(({ id: _id, ...updates }) => Object.values(updates).some((value) => value !== undefined), "At least one camera plan column field must be updated");
export const deleteCameraPlanColumnInputSchema = z.object({
    id: uuidSchema,
});
const cameraPlanOverlayFields = {
    overlayType: cameraPlanOverlayTypeSchema,
    positionX: z.number().optional(),
    positionY: z.number().optional(),
    width: z.number().optional(),
    height: z.number().optional(),
    rotation: z.number().optional(),
    color: z.string().min(1).max(100).optional(),
    opacity: z.number().optional(),
    strokeWidth: z.number().optional(),
    fill: z.boolean().optional(),
    imageURL: nullableStringSchema,
    label: nullableStringSchema,
    fontSize: z.number().optional(),
    points: z
        .array(z.object({ x: z.number(), y: z.number() }))
        .nullable()
        .optional(),
    zIndex: z.number().int().optional(),
    sortOrder: z.number().int().optional(),
    locked: z.boolean().optional(),
};
export const createCameraPlanOverlayInputSchema = z.object({
    id: uuidSchema,
    cameraPlanID: uuidSchema,
    ...cameraPlanOverlayFields,
});
export const updateCameraPlanOverlayInputSchema = z
    .object({
    id: uuidSchema,
    ...Object.fromEntries(Object.entries(cameraPlanOverlayFields).map(([key, schema]) => [
        key,
        schema.optional(),
    ])),
})
    .refine(({ id: _id, ...updates }) => Object.values(updates).some((value) => value !== undefined), "At least one camera plan overlay field must be updated");
export const deleteCameraPlanOverlayInputSchema = z.object({
    id: uuidSchema,
});
/** A camera number is just the number ("7"); its name is the description. */
export const cameraNumberSchema = z
    .string()
    .trim()
    .regex(/^\d{1,6}$/, "Camera number must be a number, e.g. 7");
/** NULL: the camera type decides (see SymbolType.profileLane). */
const profileDisplaySchema = z.enum(PROFILE_DISPLAYS).nullable().optional();
export const createCameraInputSchema = z.object({
    id: uuidSchema,
    cameraPlanID: uuidSchema,
    number: cameraNumberSchema,
    description: nullableStringSchema,
    remarks: nullableStringSchema,
    icon: z.string().min(1).max(100).optional(),
    positionX: nullableNumberSchema,
    positionY: nullableNumberSchema,
    rotation: z.number().optional(),
    track: z.any().nullable().optional(),
    customValues: z.record(z.string(), cameraCustomValueSchema).optional(),
    sortOrder: z.number().int().min(0).optional(),
    color: z.string().min(1).max(100).optional(),
    equipmentGroupID: nullableUUIDSchema,
    profileDisplay: profileDisplaySchema,
});
export const updateCameraInputSchema = z
    .object({
    id: uuidSchema,
    number: cameraNumberSchema.optional(),
    description: nullableStringSchema,
    remarks: nullableStringSchema,
    icon: z.string().min(1).max(100).optional(),
    positionX: nullableNumberSchema,
    positionY: nullableNumberSchema,
    rotation: z.number().optional(),
    track: z.any().nullable().optional(),
    customValues: z.record(z.string(), cameraCustomValueSchema).optional(),
    sortOrder: z.number().int().min(0).optional(),
    color: z.string().min(1).max(100).optional(),
    equipmentGroupID: nullableUUIDSchema,
    profileDisplay: profileDisplaySchema,
})
    .refine(({ id: _id, ...updates }) => Object.values(updates).some((value) => value !== undefined), "At least one camera field must be updated");
export const deleteCameraInputSchema = z.object({
    id: uuidSchema,
});
const lngLatSchema = z
    .array(z.number())
    .length(2)
    .refine(([lng, lat]) => Math.abs(lng) <= 180 && Math.abs(lat) <= 90, "Expected [lng, lat]");
const geoCoordSchema = z
    .array(z.number())
    .min(2)
    .max(3)
    .refine(([lng, lat]) => Math.abs(lng) <= 180 && Math.abs(lat) <= 90, "Expected [lng, lat] or [lng, lat, ele]");
const viewAreaVerticesSchema = z
    .array(z.array(z.number()).length(2))
    .min(3)
    .max(200)
    .describe("[[east_m, north_m], ...] relative to the item's point");
const profileSchema = z
    .array(z.array(z.number()).length(2))
    .max(20000)
    .describe("Smoothed [[m, ele], ...]; empty while pending");
const elevationSourceSchema = z.enum(["file", "lookup", "mixed"]);
const symbolTypeSchema = z
    .string()
    .refine((key) => SYMBOL_TYPES.some((s) => s.key === key), {
    message: "Unknown symbol type",
});
const positionLetterSchema = z.string().regex(/^[A-Z]{1,2}$/);
const cameraPlanTrackFields = {
    name: trimmedNameSchema,
    color: z.string().min(1).max(100),
    sortOrder: z.number().int().min(0).optional(),
    geometry: z.object({
        type: z.literal("LineString"),
        coordinates: z.array(geoCoordSchema).min(2).max(50000),
    }),
    lengthM: z.number().min(0),
    ascentM: nullableNumberSchema,
    descentM: nullableNumberSchema,
    minEleM: nullableNumberSchema,
    maxEleM: nullableNumberSchema,
    elevationSource: elevationSourceSchema.nullable().optional(),
    profile: profileSchema,
    reversed: z.boolean().optional(),
    sourceFileURL: nullableStringSchema,
    sourceFileName: nullableStringSchema,
};
export const createCameraPlanTrackInputSchema = z.object({
    id: uuidSchema,
    cameraPlanID: uuidSchema,
    visualizationID: uuidSchema.describe("The map visualization of the track"),
    ...cameraPlanTrackFields,
});
export const updateCameraPlanTrackInputSchema = z
    .object({
    id: uuidSchema,
    name: cameraPlanTrackFields.name.optional(),
    color: cameraPlanTrackFields.color.optional(),
    sortOrder: cameraPlanTrackFields.sortOrder,
    geometry: cameraPlanTrackFields.geometry.optional(),
    lengthM: cameraPlanTrackFields.lengthM.optional(),
    ascentM: nullableNumberSchema,
    descentM: nullableNumberSchema,
    minEleM: nullableNumberSchema,
    maxEleM: nullableNumberSchema,
    elevationSource: cameraPlanTrackFields.elevationSource,
    profile: profileSchema.optional(),
    reversed: z.boolean().optional(),
    sourceFileURL: nullableStringSchema,
    sourceFileName: nullableStringSchema,
})
    .refine(({ id: _id, ...updates }) => Object.values(updates).some((value) => value !== undefined), "At least one track field must be updated");
const cameraPlanItemFields = {
    label: nullableStringSchema,
    notes: nullableStringSchema,
    point: lngLatSchema.nullable().optional(),
    line: z.array(lngLatSchema).min(2).max(5000).nullable().optional(),
    followTrackID: nullableUUIDSchema,
    followFromM: nullableNumberSchema,
    followToM: nullableNumberSchema,
    rideRanges: z
        .array(z
        .object({
        trackId: uuidSchema,
        fromM: z.number().finite().min(0),
        toM: z.number().finite().min(0),
    })
        .strict())
        .max(50)
        .nullable()
        .optional()
        .describe("Motorbike/e-bike positions: the stretches ridden with the race, metres along each track; none = the whole route"),
    showViewAreas: z.boolean().optional(),
    sortOrder: z.number().int().optional(),
    style: z
        .object({
        color: z
            .string()
            .regex(/^#[0-9a-fA-F]{6}$/)
            .optional(),
        dash: z.enum(LINE_DASHES).optional(),
        width: z.number().min(LINE_WIDTH_MIN).max(LINE_WIDTH_MAX).optional(),
        arrow: z.boolean().optional(),
        size: z.number().min(TEXT_SIZE_MIN).max(TEXT_SIZE_MAX).optional(),
        icon: z
            .string()
            .refine((key) => MARKER_ICONS.some((i) => i.key === key), {
            message: "Unknown marker icon",
        })
            .optional(),
    })
        .strict()
        .nullable()
        .optional()
        .describe("Generic elements (line, text, marker) only"),
};
const newViewAreaSchema = z.object({
    id: uuidSchema,
    vertices: viewAreaVerticesSchema,
    label: nullableStringSchema,
});
export const createCameraPlanItemInputSchema = z
    .object({
    id: uuidSchema,
    cameraPlanID: uuidSchema,
    visualizationID: uuidSchema.describe("The map or image visualization the item is drawn on"),
    symbolType: symbolTypeSchema,
    cameraID: nullableUUIDSchema,
    positionLetter: positionLetterSchema.nullable().optional(),
    ...cameraPlanItemFields,
    newCamera: z
        .object({
        id: uuidSchema,
        number: cameraNumberSchema,
        color: z.string().min(1).max(100).optional(),
        sortOrder: z.number().int().min(0).optional(),
    })
        .optional()
        .describe("Create the camera this position belongs to (position A)"),
    viewAreas: z.array(newViewAreaSchema).max(20).optional(),
})
    .refine((args) => !!args.point || !!args.line, "An item needs a point or a line");
export const updateCameraPlanItemInputSchema = z
    .object({
    id: uuidSchema,
    symbolType: symbolTypeSchema.optional(),
    ...cameraPlanItemFields,
})
    .refine(({ id: _id, ...updates }) => Object.values(updates).some((value) => value !== undefined), "At least one item field must be updated");
export const reorderCameraPlanItemsInputSchema = z.object({
    orderedIDs: z.array(uuidSchema).max(2000),
});
export const createCameraPlanViewAreaInputSchema = z.object({
    id: uuidSchema,
    itemID: uuidSchema,
    vertices: viewAreaVerticesSchema,
    label: nullableStringSchema,
    sortOrder: z.number().int().min(0).optional(),
});
export const updateCameraPlanViewAreaInputSchema = z
    .object({
    id: uuidSchema,
    vertices: viewAreaVerticesSchema.optional(),
    label: nullableStringSchema,
    sortOrder: z.number().int().min(0).optional(),
})
    .refine(({ id: _id, ...updates }) => Object.values(updates).some((value) => value !== undefined), "At least one view area field must be updated");
export const createCameraFunctionAssignmentInputSchema = z.object({
    id: uuidSchema,
    cameraID: uuidSchema,
    functionAssignmentID: uuidSchema,
});
export const deleteCameraFunctionAssignmentInputSchema = z.object({
    id: uuidSchema,
});
async function deleteItemWithViewAreas(tx, item) {
    for (const area of item.viewAreas ?? []) {
        await tx.mutate.cameraPlanItemViewAreas.delete({ id: area.id });
    }
    await tx.mutate.cameraPlanItems.delete({ id: item.id });
}
/** A follow-track line may only follow a track of its own plan. */
async function assertTrackOfPlan(tx, trackID, cameraPlanID) {
    if (!trackID)
        return;
    const track = await tx.run(equipmentPlanZql.cameraPlanTracks.where("id", trackID).one());
    if (!track || track.cameraPlanID !== cameraPlanID) {
        throw new ValidationError("The track belongs to another plan");
    }
}
function assertEquipmentWriteAccess(ctx) {
    if (ctx.role !== "admin" && ctx.role !== "employee") {
        throw new ForbiddenError("Not allowed to edit equipment plans");
    }
}
/**
 * Project write check for rows reached through a parent. Staff skip the
 * lookup; freelancers resolve the row's project on the server (the browser
 * only holds rows the server already let them read).
 */
async function assertPlanWriteAccess(ctx, resolveProjectID) {
    if (isStaff(ctx))
        return;
    if (ctx.role === "freelancer" && ctx.clientSide)
        return;
    assertCanEditProject(ctx, await resolveProjectID());
}
/** Rejects non-staff writes linking a row of another project. */
function assertSameProject(ctx, projectID, linkedProjectID) {
    if (isStaff(ctx) || ctx.clientSide)
        return;
    if (!projectID || projectID !== linkedProjectID) {
        throw new ForbiddenError("Linked row belongs to another project");
    }
}
async function groupProjectID(tx, groupID) {
    const group = await tx.run(equipmentPlanZql.projectEquipmentGroups.where("id", groupID).one());
    return group?.projectID;
}
async function temporaryEquipmentProjectID(tx, id) {
    const row = await tx.run(equipmentPlanZql.temporaryEquipment.where("id", id).one());
    return row?.projectID;
}
async function assignmentProjectID(tx, assignmentID) {
    const assignment = await tx.run(equipmentPlanZql.projectEquipmentAssignments
        .where("id", assignmentID)
        .one());
    return assignment ? groupProjectID(tx, assignment.groupID) : undefined;
}
async function instanceAssignmentProjectID(tx, id) {
    const row = await tx.run(equipmentPlanZql.projectEquipmentInstanceAssignments.where("id", id).one());
    return row ? assignmentProjectID(tx, row.assignmentID) : undefined;
}
async function loadVisualization(tx, id) {
    const viz = await tx.run(equipmentPlanZql.cameraPlanVisualizations.where("id", id).one());
    if (!viz)
        throw new NotFoundError("Visualization not found");
    return viz;
}
/** A visualization of the plan that can hold items (or tracks: maps only). */
async function assertVisualizationOfPlan(tx, visualizationID, cameraPlanID, types) {
    const viz = await loadVisualization(tx, visualizationID);
    if (viz.cameraPlanID !== cameraPlanID) {
        throw new ValidationError("The visualization belongs to another plan");
    }
    if (!types.includes(viz.vizType)) {
        throw new ValidationError(`A ${viz.vizType} visualization can't hold this`);
    }
    return viz;
}
async function cameraPlanProjectID(tx, cameraPlanID) {
    const plan = await tx.run(equipmentPlanZql.cameraPlans.where("id", cameraPlanID).one());
    return plan?.projectID;
}
async function cameraPlanChildProjectID(tx, table, id) {
    const query = equipmentPlanZql[table];
    const row = await tx.run(query.where("id", id).one());
    return row ? cameraPlanProjectID(tx, row.cameraPlanID) : undefined;
}
async function cameraFunctionAssignmentProjectID(tx, id) {
    const row = await tx.run(equipmentPlanZql.cameraFunctionAssignments.where("id", id).one());
    return row
        ? cameraPlanChildProjectID(tx, "cameras", row.cameraID)
        : undefined;
}
/** Enforces the tag hierarchy rules (max depth, no cycles). */
async function assertValidEquipmentTagParent(tx, tagId, parentId) {
    if (!parentId)
        return;
    const tags = await tx.run(equipmentPlanZql.equipmentTags);
    const error = validateEquipmentTagParent(tags ?? [], tagId, parentId);
    if (error)
        throw new Error(error);
}
/** Writes the staff-only price row of an equipment item (partial upsert). */
async function upsertEquipmentPrices(tx, equipmentID, prices) {
    await tx.mutate.equipmentPrices.upsert({
        equipmentID,
        ...(prices.newPrice !== undefined ? { newPrice: prices.newPrice } : {}),
        ...(prices.rentalPrice !== undefined
            ? { rentalPrice: prices.rentalPrice }
            : {}),
    });
}
async function replaceEquipmentTagAssignments(tx, equipmentID, tagIDs) {
    const existing = await tx.run(equipmentPlanZql.equipmentTagAssignments.where("equipmentID", equipmentID));
    for (const assignment of existing ?? []) {
        await tx.mutate.equipmentTagAssignments.delete({ id: assignment.id });
    }
    for (const tagID of tagIDs) {
        await tx.mutate.equipmentTagAssignments.insert({
            id: crypto.randomUUID(),
            equipmentID,
            tagID,
        });
    }
}
export const equipmentPlanMutatorDefinitions = {
    equipmentTags: {
        create: defineEquipmentMutator(createEquipmentTagInputSchema, async ({ tx, ctx, args, }) => {
            assertEquipmentWriteAccess(ctx);
            await assertValidEquipmentTagParent(tx, null, args.parentID ?? null);
            await tx.mutate.equipmentTags.insert({
                id: args.id,
                name: args.name,
                parentID: args.parentID ?? null,
                icon: args.icon || null,
            });
        }),
        update: defineEquipmentMutator(updateEquipmentTagInputSchema, async ({ tx, ctx, args, }) => {
            assertEquipmentWriteAccess(ctx);
            if (args.parentID !== undefined) {
                await assertValidEquipmentTagParent(tx, args.id, args.parentID);
            }
            await tx.mutate.equipmentTags.update({
                id: args.id,
                ...(args.name !== undefined ? { name: args.name } : {}),
                ...(args.parentID !== undefined ? { parentID: args.parentID } : {}),
                ...(args.icon !== undefined ? { icon: args.icon || null } : {}),
            });
        }),
        delete: defineEquipmentMutator({
            input: equipmentTagIDInputSchema,
            description: "Hard-delete an equipment tag and its whole subtree.",
            destructive: true,
        }, async ({ tx, ctx, args, }) => {
            assertEquipmentWriteAccess(ctx);
            await tx.mutate.equipmentTags.delete({ id: args.id });
        }),
    },
    equipmentCatalog: {
        create: defineEquipmentMutator(createEquipmentCatalogInputSchema, async ({ tx, ctx, args, }) => {
            assertEquipmentWriteAccess(ctx);
            const { tagIDs = [], newPrice, rentalPrice, ...equipmentArgs } = args;
            await tx.mutate.equipment.insert({
                ...equipmentArgs,
                isSerialized: equipmentArgs.isSerialized ?? true,
                stockQuantity: equipmentArgs.isSerialized
                    ? 0
                    : (equipmentArgs.stockQuantity ?? 0),
            });
            if (newPrice != null || rentalPrice != null) {
                await upsertEquipmentPrices(tx, args.id, { newPrice, rentalPrice });
            }
            await replaceEquipmentTagAssignments(tx, args.id, tagIDs);
        }),
        update: defineEquipmentMutator(updateEquipmentCatalogInputSchema, async ({ tx, ctx, args, }) => {
            assertEquipmentWriteAccess(ctx);
            const { tagIDs, newPrice, rentalPrice, ...equipmentArgs } = args;
            const equipmentUpdate = {
                ...equipmentArgs,
                ...(equipmentArgs.isSerialized === true ? { stockQuantity: 0 } : {}),
            };
            if (Object.entries(equipmentUpdate).some(([key, value]) => key !== "id" && value !== undefined)) {
                await tx.mutate.equipment.update(equipmentUpdate);
            }
            if (newPrice !== undefined || rentalPrice !== undefined) {
                await upsertEquipmentPrices(tx, args.id, { newPrice, rentalPrice });
            }
            if (tagIDs !== undefined) {
                await replaceEquipmentTagAssignments(tx, args.id, tagIDs);
            }
        }),
        softDelete: defineEquipmentMutator(softDeleteEquipmentCatalogInputSchema, async ({ tx, ctx, args, }) => {
            assertEquipmentWriteAccess(ctx);
            await tx.mutate.equipment.update({
                id: args.id,
                deletedAt: Date.now(),
            });
        }),
    },
    equipmentInstances: {
        create: defineEquipmentMutator(createEquipmentInstanceInputSchema, async ({ tx, ctx, args, }) => {
            assertEquipmentWriteAccess(ctx);
            await tx.mutate.equipmentInstances.insert({
                id: args.id,
                equipmentID: args.equipmentID,
                identifier: args.identifier,
                notes: args.notes ?? null,
                acquisitionDate: toZeroDate(args.acquisitionDate) ?? null,
                vendorID: args.vendorID ?? null,
                status: args.status ?? "active",
                qrCode: args.qrCode ?? null,
                serialNumber: args.serialNumber ?? null,
            });
            if (args.acquisitionPrice != null) {
                await tx.mutate.equipmentInstancePrices.upsert({
                    instanceID: args.id,
                    acquisitionPrice: args.acquisitionPrice,
                });
            }
        }),
        update: defineEquipmentMutator(updateEquipmentInstanceInputSchema, async ({ tx, ctx, args, }) => {
            assertEquipmentWriteAccess(ctx);
            const { acquisitionPrice, ...instanceArgs } = args;
            const instanceUpdate = {
                ...instanceArgs,
                acquisitionDate: instanceArgs.acquisitionDate !== undefined
                    ? (toZeroDate(instanceArgs.acquisitionDate) ?? null)
                    : undefined,
            };
            if (Object.entries(instanceUpdate).some(([key, value]) => key !== "id" && value !== undefined)) {
                await tx.mutate.equipmentInstances.update(instanceUpdate);
            }
            if (acquisitionPrice !== undefined) {
                await tx.mutate.equipmentInstancePrices.upsert({
                    instanceID: args.id,
                    acquisitionPrice,
                });
            }
        }),
        delete: defineEquipmentMutator(deleteEquipmentInstanceInputSchema, async ({ tx, ctx, args, }) => {
            assertEquipmentWriteAccess(ctx);
            await tx.mutate.equipmentInstances.delete({ id: args.id });
        }),
    },
    equipmentImages: {
        create: defineEquipmentMutator(createEquipmentImageInputSchema, async ({ tx, ctx, args, }) => {
            assertEquipmentWriteAccess(ctx);
            await tx.mutate.equipmentImages.insert({
                id: args.id,
                equipmentID: args.equipmentID,
                imageURL: args.imageURL,
                displayOrder: args.displayOrder ?? null,
            });
        }),
        delete: defineEquipmentMutator(deleteEquipmentImageInputSchema, async ({ tx, ctx, args, }) => {
            assertEquipmentWriteAccess(ctx);
            await tx.mutate.equipmentImages.delete({ id: args.id });
        }),
    },
    equipmentContents: {
        add: defineEquipmentMutator(addEquipmentContentInputSchema, async ({ tx, ctx, args, }) => {
            assertEquipmentWriteAccess(ctx);
            await tx.mutate.equipmentContents.insert({
                id: args.id,
                parentEquipmentID: args.parentEquipmentID,
                childEquipmentID: args.childEquipmentID ?? null,
                name: args.childEquipmentID ? null : (args.name ?? null),
                quantity: args.quantity ?? 1,
            });
        }),
        updateName: defineEquipmentMutator(updateEquipmentContentNameInputSchema, async ({ tx, ctx, args, }) => {
            assertEquipmentWriteAccess(ctx);
            const content = await tx.run(equipmentPlanZql.equipmentContents.where("id", args.id).one());
            if (!content)
                throw new NotFoundError("Content not found");
            if (content.childEquipmentID) {
                throw new ForbiddenError("Only free content lines can be renamed");
            }
            await tx.mutate.equipmentContents.update(args);
        }),
        updateQuantity: defineEquipmentMutator(updateEquipmentContentQuantityInputSchema, async ({ tx, ctx, args, }) => {
            assertEquipmentWriteAccess(ctx);
            await tx.mutate.equipmentContents.update(args);
        }),
        remove: defineEquipmentMutator(removeEquipmentContentInputSchema, async ({ tx, ctx, args, }) => {
            assertEquipmentWriteAccess(ctx);
            await tx.mutate.equipmentContents.delete({ id: args.id });
        }),
    },
    equipmentPlan: {
        createGroup: defineEquipmentMutator(createEquipmentGroupInputSchema, async ({ tx, ctx, args, }) => {
            assertCanEditProject(ctx, args.projectID);
            await tx.mutate.projectEquipmentGroups.insert({
                id: args.id,
                projectID: args.projectID,
                name: args.name,
                packingState: "open",
                riggingState: "open",
                displayOrder: args.displayOrder ?? 0,
                isCollapsed: false,
            });
        }),
        updateGroup: defineEquipmentMutator(updateEquipmentGroupInputSchema, async ({ tx, ctx, args, }) => {
            await assertPlanWriteAccess(ctx, () => groupProjectID(tx, args.id));
            await tx.mutate.projectEquipmentGroups.update(args);
        }),
        deleteGroup: defineEquipmentMutator({
            input: deleteEquipmentGroupInputSchema,
            description: "Hard-delete an equipment plan group with all its assignments.",
            destructive: true,
        }, async ({ tx, ctx, args, }) => {
            await assertPlanWriteAccess(ctx, () => groupProjectID(tx, args.id));
            await tx.mutate.projectEquipmentGroups.delete({ id: args.id });
        }),
        createTemporaryEquipment: defineEquipmentMutator(createTemporaryEquipmentInputSchema, async ({ tx, ctx, args, }) => {
            assertCanEditProject(ctx, args.projectID);
            await tx.mutate.temporaryEquipment.insert(args);
        }),
        updateTemporaryEquipment: defineEquipmentMutator(updateTemporaryEquipmentInputSchema, async ({ tx, ctx, args }) => {
            const { id, ...updates } = args;
            if (!id)
                throw new Error("Temporary equipment id is required");
            await assertPlanWriteAccess(ctx, () => temporaryEquipmentProjectID(tx, id));
            await tx.mutate.temporaryEquipment.update({
                ...updates,
                id,
            });
        }),
        createAssignment: defineEquipmentMutator(createEquipmentAssignmentInputSchema, async ({ tx, ctx, args, }) => {
            await assertPlanWriteAccess(ctx, async () => {
                const projectID = await groupProjectID(tx, args.groupID);
                if (args.temporaryEquipmentID) {
                    assertSameProject(ctx, projectID, await temporaryEquipmentProjectID(tx, args.temporaryEquipmentID));
                }
                return projectID;
            });
            await tx.mutate.projectEquipmentAssignments.insert({
                id: args.id,
                groupID: args.groupID,
                equipmentID: args.equipmentID ?? null,
                temporaryEquipmentID: args.temporaryEquipmentID ?? null,
                amount: args.amount ?? 1,
                notes: args.notes ?? null,
                cameraPlanColumnKey: args.cameraPlanColumnKey ?? null,
            });
        }),
        updateAssignment: defineEquipmentMutator(updateEquipmentAssignmentInputSchema, async ({ tx, ctx, args, }) => {
            await assertPlanWriteAccess(ctx, async () => {
                const projectID = await assignmentProjectID(tx, args.id);
                if (args.temporaryEquipmentID) {
                    assertSameProject(ctx, projectID, await temporaryEquipmentProjectID(tx, args.temporaryEquipmentID));
                }
                return projectID;
            });
            await tx.mutate.projectEquipmentAssignments.update(args);
        }),
        deleteAssignment: defineEquipmentMutator(deleteEquipmentAssignmentInputSchema, async ({ tx, ctx, args, }) => {
            await assertPlanWriteAccess(ctx, () => assignmentProjectID(tx, args.id));
            await tx.mutate.projectEquipmentAssignments.delete({ id: args.id });
        }),
        assignInstance: defineEquipmentMutator(assignEquipmentInstanceInputSchema, async ({ tx, ctx, args, }) => {
            await assertPlanWriteAccess(ctx, () => assignmentProjectID(tx, args.assignmentID));
            await tx.mutate.projectEquipmentInstanceAssignments.insert(args);
        }),
        unassignInstance: defineEquipmentMutator(unassignEquipmentInstanceInputSchema, async ({ tx, ctx, args, }) => {
            await assertPlanWriteAccess(ctx, () => instanceAssignmentProjectID(tx, args.id));
            await tx.mutate.projectEquipmentInstanceAssignments.delete({
                id: args.id,
            });
        }),
    },
    cameraPlans: {
        createPlan: defineEquipmentMutator(createCameraPlanInputSchema, async ({ tx, ctx, args, }) => {
            assertCanEditProject(ctx, args.projectID);
            await tx.mutate.cameraPlans.insert({
                id: args.id,
                projectID: args.projectID,
                name: args.name,
                description: args.description ?? null,
                eventDate: toZeroDate(args.eventDate) ?? null,
                sortOrder: args.sortOrder ?? 0,
                subtitle: args.subtitle ?? null,
                markSnapDistanceM: 100,
                markMergeDistanceM: 200,
            });
            if (args.visualization) {
                await tx.mutate.cameraPlanVisualizations.insert({
                    id: args.visualization.id,
                    cameraPlanID: args.id,
                    name: args.visualization.name,
                    vizType: args.visualization.vizType,
                    imageURL: null,
                    imageAspect: null,
                    embedURL: args.visualization.embedURL ?? null,
                    viewDefaults: args.visualization.viewDefaults ?? {},
                    convertLegacy: false,
                    sortOrder: 0,
                });
            }
        }),
        updatePlan: defineEquipmentMutator(updateCameraPlanInputSchema, async ({ tx, ctx, args, }) => {
            await assertPlanWriteAccess(ctx, () => cameraPlanProjectID(tx, args.id));
            await tx.mutate.cameraPlans.update({
                ...args,
                eventDate: toZeroDate(args.eventDate),
            });
        }),
        createVisualization: defineEquipmentMutator({
            input: createCameraPlanVisualizationInputSchema,
            description: "Add a visualization (map, image or embedded page) to a camera plan. Its cameras are shared; positions are per visualization.",
        }, async ({ tx, ctx, args, }) => {
            await assertPlanWriteAccess(ctx, () => cameraPlanProjectID(tx, args.cameraPlanID));
            await tx.mutate.cameraPlanVisualizations.insert({
                id: args.id,
                cameraPlanID: args.cameraPlanID,
                name: args.name,
                vizType: args.vizType,
                imageURL: args.imageURL ?? null,
                imageAspect: args.imageAspect ?? null,
                embedURL: args.embedURL ?? null,
                viewDefaults: args.viewDefaults ?? {},
                convertLegacy: false,
                sortOrder: args.sortOrder ?? 0,
            });
        }),
        updateVisualization: defineEquipmentMutator({
            input: updateCameraPlanVisualizationInputSchema,
            description: "Rename a visualization, replace its image or embed URL, or save its default view.",
        }, async ({ tx, ctx, args, }) => {
            const viz = await loadVisualization(tx, args.id);
            await assertPlanWriteAccess(ctx, () => cameraPlanProjectID(tx, viz.cameraPlanID));
            await tx.mutate.cameraPlanVisualizations.update(args);
        }),
        deleteVisualization: defineEquipmentMutator({
            input: deleteCameraPlanInputSchema,
            description: "Delete a visualization with its positions, drawings and tracks. The plan's cameras stay.",
            destructive: true,
        }, async ({ tx, ctx, args, }) => {
            const viz = await loadVisualization(tx, args.id);
            await assertPlanWriteAccess(ctx, () => cameraPlanProjectID(tx, viz.cameraPlanID));
            await tx.mutate.cameraPlanVisualizations.delete({ id: args.id });
        }),
        adoptImageVisualization: defineEquipmentMutator({
            input: adoptImageVisualizationInputSchema,
            description: "Store the measured aspect ratio of an image visualization. Visualizations of plans from the old image editor also get their placed cameras and overlays as items; only the first call converts.",
        }, async ({ tx, ctx, args, }) => {
            const viz = await loadVisualization(tx, args.id);
            const planID = viz.cameraPlanID;
            await assertPlanWriteAccess(ctx, () => cameraPlanProjectID(tx, planID));
            if (viz.vizType !== "image") {
                throw new ValidationError("Only image visualizations have a frame");
            }
            if (viz.imageAspect != null)
                return;
            const aspect = clampImageAspect(args.imageAspect);
            await tx.mutate.cameraPlanVisualizations.update({
                id: args.id,
                imageAspect: aspect,
                convertLegacy: false,
            });
            if (!viz.convertLegacy)
                return;
            const items = await tx.run(equipmentPlanZql.cameraPlanItems.where("visualizationID", args.id));
            const placed = new Set(items.map((i) => i.cameraID));
            let sortOrder = items.length;
            const base = {
                cameraPlanID: planID,
                visualizationID: args.id,
                notes: null,
                followTrackID: null,
                followFromM: null,
                followToM: null,
                showViewAreas: false,
            };
            const cameras = await tx.run(equipmentPlanZql.cameras.where("cameraPlanID", planID));
            for (const camera of cameras) {
                if (camera.positionX == null || camera.positionY == null)
                    continue;
                if (placed.has(camera.id))
                    continue;
                // Deterministic ids: client and server create the same rows
                await tx.mutate.cameraPlanItems.insert({
                    ...base,
                    id: camera.id,
                    symbolType: resolveCameraType(camera.icon),
                    cameraID: camera.id,
                    positionLetter: "A",
                    label: null,
                    point: imageFractionToLngLat(camera.positionX, camera.positionY, aspect),
                    line: null,
                    sortOrder: sortOrder++,
                    style: null,
                });
            }
            const overlays = await tx.run(equipmentPlanZql.cameraPlanOverlays.where("cameraPlanID", planID));
            for (const overlay of overlays) {
                const item = legacyOverlayToItem(overlay, aspect);
                if (!item)
                    continue;
                await tx.mutate.cameraPlanItems.insert({
                    ...base,
                    id: overlay.id,
                    symbolType: item.symbolType,
                    cameraID: null,
                    positionLetter: null,
                    label: item.label,
                    point: item.point,
                    line: item.line,
                    sortOrder: sortOrder++,
                    style: item.style,
                });
            }
        }),
        deletePlan: defineEquipmentMutator({
            input: deleteCameraPlanInputSchema,
            description: "Hard-delete a camera plan with its cameras, columns and overlays.",
            destructive: true,
        }, async ({ tx, ctx, args, }) => {
            await assertPlanWriteAccess(ctx, () => cameraPlanProjectID(tx, args.id));
            await tx.mutate.cameraPlans.delete({ id: args.id });
        }),
        createColumn: defineEquipmentMutator(createCameraPlanColumnInputSchema, async ({ tx, ctx, args, }) => {
            await assertPlanWriteAccess(ctx, () => cameraPlanProjectID(tx, args.cameraPlanID));
            await tx.mutate.cameraPlanColumns.insert({
                id: args.id,
                cameraPlanID: args.cameraPlanID,
                name: args.name,
                columnType: args.columnType,
                options: args.options ?? [],
                sortOrder: args.sortOrder ?? 0,
            });
        }),
        updateColumn: defineEquipmentMutator(updateCameraPlanColumnInputSchema, async ({ tx, ctx, args, }) => {
            await assertPlanWriteAccess(ctx, () => cameraPlanChildProjectID(tx, "cameraPlanColumns", args.id));
            await tx.mutate.cameraPlanColumns.update(args);
        }),
        deleteColumn: defineEquipmentMutator(deleteCameraPlanColumnInputSchema, async ({ tx, ctx, args, }) => {
            await assertPlanWriteAccess(ctx, () => cameraPlanChildProjectID(tx, "cameraPlanColumns", args.id));
            await tx.mutate.cameraPlanColumns.delete({ id: args.id });
        }),
        createOverlay: defineEquipmentMutator(createCameraPlanOverlayInputSchema, async ({ tx, ctx, args, }) => {
            await assertPlanWriteAccess(ctx, () => cameraPlanProjectID(tx, args.cameraPlanID));
            await tx.mutate.cameraPlanOverlays.insert({
                id: args.id,
                cameraPlanID: args.cameraPlanID,
                overlayType: args.overlayType,
                positionX: args.positionX ?? 0.5,
                positionY: args.positionY ?? 0.5,
                width: args.width ?? 0.1,
                height: args.height ?? 0.1,
                rotation: args.rotation ?? 0,
                color: args.color ?? "#6b7280",
                opacity: args.opacity ?? 1,
                strokeWidth: args.strokeWidth ?? 2,
                fill: args.fill ?? false,
                imageURL: args.imageURL ?? null,
                label: args.label ?? null,
                fontSize: args.fontSize ?? 14,
                points: args.points ?? null,
                zIndex: args.zIndex ?? 0,
                sortOrder: args.sortOrder ?? 0,
                locked: args.locked ?? false,
            });
        }),
        updateOverlay: defineEquipmentMutator(updateCameraPlanOverlayInputSchema, async ({ tx, ctx, args, }) => {
            await assertPlanWriteAccess(ctx, () => cameraPlanChildProjectID(tx, "cameraPlanOverlays", args.id));
            await tx.mutate.cameraPlanOverlays.update(args);
        }),
        deleteOverlay: defineEquipmentMutator(deleteCameraPlanOverlayInputSchema, async ({ tx, ctx, args, }) => {
            await assertPlanWriteAccess(ctx, () => cameraPlanChildProjectID(tx, "cameraPlanOverlays", args.id));
            await tx.mutate.cameraPlanOverlays.delete({ id: args.id });
        }),
        createCamera: defineEquipmentMutator(createCameraInputSchema, async ({ tx, ctx, args, }) => {
            await assertPlanWriteAccess(ctx, async () => {
                const projectID = await cameraPlanProjectID(tx, args.cameraPlanID);
                if (args.equipmentGroupID) {
                    assertSameProject(ctx, projectID, await groupProjectID(tx, args.equipmentGroupID));
                }
                return projectID;
            });
            await tx.mutate.cameras.insert({
                id: args.id,
                cameraPlanID: args.cameraPlanID,
                number: args.number,
                description: args.description ?? null,
                remarks: args.remarks ?? null,
                icon: resolveCameraType(args.icon),
                positionX: args.positionX ?? null,
                positionY: args.positionY ?? null,
                rotation: args.rotation ?? 0,
                track: args.track ?? null,
                customValues: args.customValues ?? {},
                sortOrder: args.sortOrder ?? 0,
                color: args.color ?? "#3b82f6",
                equipmentGroupID: args.equipmentGroupID ?? null,
                profileDisplay: args.profileDisplay ?? null,
            });
        }),
        updateCamera: defineEquipmentMutator(updateCameraInputSchema, async ({ tx, ctx, args, }) => {
            await assertPlanWriteAccess(ctx, async () => {
                const projectID = await cameraPlanChildProjectID(tx, "cameras", args.id);
                if (args.equipmentGroupID) {
                    assertSameProject(ctx, projectID, await groupProjectID(tx, args.equipmentGroupID));
                }
                return projectID;
            });
            if (args.icon !== undefined) {
                const icon = resolveCameraType(args.icon);
                await tx.mutate.cameras.update({ ...args, icon });
                // Camera positions on geo plans mirror the camera's type
                const items = await tx.run(equipmentPlanZql.cameraPlanItems.where("cameraID", args.id));
                for (const item of items) {
                    if (item.symbolType !== icon) {
                        await tx.mutate.cameraPlanItems.update({
                            id: item.id,
                            symbolType: icon,
                        });
                    }
                }
                return;
            }
            await tx.mutate.cameras.update(args);
        }),
        deleteCamera: defineEquipmentMutator({
            input: deleteCameraInputSchema,
            description: "Delete a camera with its operator links and its positions on geo plans.",
        }, async ({ tx, ctx, args, }) => {
            await assertPlanWriteAccess(ctx, () => cameraPlanChildProjectID(tx, "cameras", args.id));
            const items = await tx.run(equipmentPlanZql.cameraPlanItems
                .where("cameraID", args.id)
                .related("viewAreas"));
            for (const item of items) {
                await deleteItemWithViewAreas(tx, item);
            }
            await tx.mutate.cameras.delete({ id: args.id });
        }),
        createTrack: defineEquipmentMutator({
            input: createCameraPlanTrackInputSchema,
            description: "Add a track (course line with elevation profile) to a geo camera plan.",
        }, async ({ tx, ctx, args, }) => {
            assertEquipmentWriteAccess(ctx);
            await assertVisualizationOfPlan(tx, args.visualizationID, args.cameraPlanID, ["geo"]);
            await tx.mutate.cameraPlanTracks.insert({
                id: args.id,
                cameraPlanID: args.cameraPlanID,
                visualizationID: args.visualizationID,
                name: args.name,
                color: args.color,
                sortOrder: args.sortOrder ?? 0,
                geometry: args.geometry,
                lengthM: args.lengthM,
                ascentM: args.ascentM ?? null,
                descentM: args.descentM ?? null,
                minEleM: args.minEleM ?? null,
                maxEleM: args.maxEleM ?? null,
                elevationSource: args.elevationSource ?? null,
                profile: args.profile,
                reversed: args.reversed ?? false,
                sourceFileURL: args.sourceFileURL ?? null,
                sourceFileName: args.sourceFileName ?? null,
            });
        }),
        updateTrack: defineEquipmentMutator(updateCameraPlanTrackInputSchema, async ({ tx, ctx, args, }) => {
            assertEquipmentWriteAccess(ctx);
            await tx.mutate.cameraPlanTracks.update(args);
        }),
        deleteTrack: defineEquipmentMutator(deleteCameraInputSchema, async ({ tx, ctx, args, }) => {
            assertEquipmentWriteAccess(ctx);
            const following = await tx.run(equipmentPlanZql.cameraPlanItems.where("followTrackID", args.id));
            for (const item of following) {
                await tx.mutate.cameraPlanItems.update({
                    id: item.id,
                    followTrackID: null,
                });
            }
            // Stretches ridden on the track go with it
            const track = await tx.run(equipmentPlanZql.cameraPlanTracks.where("id", args.id).one());
            if (track) {
                const items = await tx.run(equipmentPlanZql.cameraPlanItems.where("cameraPlanID", track.cameraPlanID));
                for (const item of items) {
                    const ranges = item.rideRanges ?? [];
                    if (!ranges.some((r) => r.trackId === args.id))
                        continue;
                    const rest = ranges.filter((r) => r.trackId !== args.id);
                    await tx.mutate.cameraPlanItems.update({
                        id: item.id,
                        rideRanges: rest.length ? rest : null,
                    });
                }
            }
            await tx.mutate.cameraPlanTracks.delete({ id: args.id });
        }),
        createItem: defineEquipmentMutator({
            input: createCameraPlanItemInputSchema,
            description: "Place a symbol on a geo camera plan. Camera symbols need cameraID (a further position, lettered automatically) or newCamera (creates the camera as position A).",
        }, async ({ tx, ctx, args, }) => {
            assertEquipmentWriteAccess(ctx);
            const symbol = getSymbolType(args.symbolType);
            await assertVisualizationOfPlan(tx, args.visualizationID, args.cameraPlanID, ["geo", "image"]);
            let cameraID = args.cameraID ?? null;
            let positionLetter = args.positionLetter ?? null;
            let symbolType = symbol.key;
            if (args.newCamera) {
                cameraID = args.newCamera.id;
                positionLetter = "A";
                await tx.mutate.cameras.insert({
                    id: args.newCamera.id,
                    cameraPlanID: args.cameraPlanID,
                    number: args.newCamera.number,
                    description: null,
                    remarks: null,
                    icon: resolveCameraType(symbol.key),
                    positionX: null,
                    positionY: null,
                    rotation: 0,
                    track: null,
                    customValues: {},
                    sortOrder: args.newCamera.sortOrder ?? 0,
                    color: args.newCamera.color ?? "#3b82f6",
                    equipmentGroupID: null,
                });
            }
            else if (cameraID) {
                const camera = await tx.run(equipmentPlanZql.cameras.where("id", cameraID).one());
                if (!camera)
                    throw new NotFoundError("Camera not found");
                if (camera.cameraPlanID !== args.cameraPlanID) {
                    throw new ValidationError("The camera belongs to another plan");
                }
                symbolType = resolveCameraType(camera.icon);
                if (!positionLetter) {
                    const positions = await tx.run(equipmentPlanZql.cameraPlanItems
                        .where("cameraID", cameraID)
                        .where("visualizationID", args.visualizationID));
                    positionLetter = nextPositionLetter(positions.map((p) => p.positionLetter));
                }
            }
            await assertTrackOfPlan(tx, args.followTrackID, args.cameraPlanID);
            for (const range of args.rideRanges ?? []) {
                await assertTrackOfPlan(tx, range.trackId, args.cameraPlanID);
            }
            if (symbol.isCamera !== !!cameraID) {
                throw new ValidationError(symbol.isCamera
                    ? "A camera symbol needs a camera"
                    : "Only camera symbols can belong to a camera");
            }
            await tx.mutate.cameraPlanItems.insert({
                id: args.id,
                cameraPlanID: args.cameraPlanID,
                symbolType,
                cameraID,
                positionLetter,
                label: cameraID ? null : (args.label ?? null),
                notes: args.notes ?? null,
                point: args.point ?? null,
                line: args.line ?? null,
                followTrackID: args.followTrackID ?? null,
                followFromM: args.followFromM ?? null,
                followToM: args.followToM ?? null,
                rideRanges: args.rideRanges ?? null,
                showViewAreas: args.showViewAreas ?? false,
                sortOrder: args.sortOrder ?? 0,
                style: symbol.drawing ? (args.style ?? null) : null,
                visualizationID: args.visualizationID,
            });
            for (const [index, area] of (args.viewAreas ?? []).entries()) {
                await tx.mutate.cameraPlanItemViewAreas.insert({
                    id: area.id,
                    itemID: args.id,
                    vertices: area.vertices,
                    label: area.label ?? null,
                    sortOrder: index,
                });
            }
        }),
        updateItem: defineEquipmentMutator(updateCameraPlanItemInputSchema, async ({ tx, ctx, args, }) => {
            assertEquipmentWriteAccess(ctx);
            const item = await tx.run(equipmentPlanZql.cameraPlanItems.where("id", args.id).one());
            if (!item)
                throw new NotFoundError("Item not found");
            if (args.symbolType !== undefined &&
                args.symbolType !== item.symbolType) {
                // Camera positions mirror their camera's type (updateCamera)
                const from = getSymbolType(item.symbolType);
                const to = getSymbolType(args.symbolType);
                if (item.cameraID || to.isCamera || from.kind !== to.kind) {
                    throw new ValidationError(item.cameraID
                        ? "Change a camera position's type on its camera"
                        : "A symbol can only become another symbol of the same kind");
                }
            }
            if (args.style != null &&
                !getSymbolType(args.symbolType ?? item.symbolType).drawing) {
                throw new ValidationError("Only drawn elements have a style");
            }
            await assertTrackOfPlan(tx, args.followTrackID, item.cameraPlanID);
            for (const range of args.rideRanges ?? []) {
                await assertTrackOfPlan(tx, range.trackId, item.cameraPlanID);
            }
            await tx.mutate.cameraPlanItems.update(args);
        }),
        deleteItem: defineEquipmentMutator({
            input: deleteCameraInputSchema,
            description: "Remove a symbol or camera position from a geo plan. The camera itself stays.",
        }, async ({ tx, ctx, args, }) => {
            assertEquipmentWriteAccess(ctx);
            const item = await tx.run(equipmentPlanZql.cameraPlanItems
                .where("id", args.id)
                .related("viewAreas")
                .one());
            if (item)
                await deleteItemWithViewAreas(tx, item);
        }),
        reorderItems: defineEquipmentMutator({
            input: reorderCameraPlanItemsInputSchema,
            description: "Set the list order of geo plan items.",
        }, async ({ tx, ctx, args, }) => {
            assertEquipmentWriteAccess(ctx);
            for (const [index, id] of args.orderedIDs.entries()) {
                await tx.mutate.cameraPlanItems.update({ id, sortOrder: index });
            }
        }),
        createViewArea: defineEquipmentMutator(createCameraPlanViewAreaInputSchema, async ({ tx, ctx, args, }) => {
            assertEquipmentWriteAccess(ctx);
            await tx.mutate.cameraPlanItemViewAreas.insert({
                id: args.id,
                itemID: args.itemID,
                vertices: args.vertices,
                label: args.label ?? null,
                sortOrder: args.sortOrder ?? 0,
            });
        }),
        updateViewArea: defineEquipmentMutator(updateCameraPlanViewAreaInputSchema, async ({ tx, ctx, args, }) => {
            assertEquipmentWriteAccess(ctx);
            await tx.mutate.cameraPlanItemViewAreas.update(args);
        }),
        deleteViewArea: defineEquipmentMutator(deleteCameraInputSchema, async ({ tx, ctx, args, }) => {
            assertEquipmentWriteAccess(ctx);
            await tx.mutate.cameraPlanItemViewAreas.delete({ id: args.id });
        }),
        addOperator: defineEquipmentMutator(createCameraFunctionAssignmentInputSchema, async ({ tx, ctx, args, }) => {
            await assertPlanWriteAccess(ctx, () => cameraPlanChildProjectID(tx, "cameras", args.cameraID));
            await tx.mutate.cameraFunctionAssignments.insert(args);
        }),
        removeOperator: defineEquipmentMutator(deleteCameraFunctionAssignmentInputSchema, async ({ tx, ctx, args, }) => {
            await assertPlanWriteAccess(ctx, () => cameraFunctionAssignmentProjectID(tx, args.id));
            await tx.mutate.cameraFunctionAssignments.delete({ id: args.id });
        }),
    },
};
export const equipmentPlanMutators = defineMutators(equipmentPlanMutatorDefinitions);
export const equipmentDomainDescriptions = describeDomains({
    equipmentTags: "Equipment categories (tree of tags)",
    equipmentCatalog: "Equipment models in the catalog",
    equipmentInstances: "Physical equipment items (serial numbers, codes)",
    equipmentContents: "Contents of cases and kits",
    equipmentConflicts: "Double bookings of equipment across projects",
    equipmentImages: "Images of equipment catalog entries",
    equipmentPlan: "Equipment planned for a project (groups, assignments, temporary items)",
    cameraPlans: "Camera plans of a project (cameras, columns, overlays, operators; geo plans with tracks, positions and view areas)",
    sims: "SIM cards and mobile data usage",
});
